<template>
  <div class="flex flex-col h-full">
    <!-- Device Configuration -->
    <div class="flex-1 overflow-y-auto p-6">
      <!-- Loading State -->
      <div v-if="loading" class="flex items-center justify-center py-12">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>

      <!-- General Section -->
      <div v-else class="space-y-2">
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >{{ tt('Device Type', 'Tip naprave') }} *</label
          >
          <select
            v-model="selectedClassId"
            @change="onClassChange"
            :disabled="mode === 'edit'"
            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50">
            <option :value="null" disabled>{{ tt('Select a device type...', 'Izberite tip naprave...') }}</option>
            <optgroup v-for="(classes, category) in classesByCategory" :key="category" :label="categoryLabel(category)">
              <option v-for="deviceClass in classes" :key="deviceClass.classId" :value="deviceClass.classId">
                {{ deviceClass.name }}
              </option>
            </optgroup>
          </select>
          <p v-if="selectedClass" class="mt-2 text-xs text-gray-500 dark:text-gray-400">
            <span class="material-symbols-outlined text-sm align-middle">{{ selectedClass.icon }}</span>
            {{ selectedClass.description }} • {{ selectedClass.values?.length || 0 }}
            {{
              tt(
                'value(s)',
                slCount(selectedClass.values?.length || 0, 'vrednost', 'vrednosti', 'vrednosti', 'vrednosti'),
              )
            }}
          </p>
        </div>

        <div>
          <label class="flex items-center gap-2">
            <input v-model="formData.enabled" type="checkbox" class="w-4 h-4 text-blue-600 border-gray-300 rounded" />
            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">{{
              tt('Device Enabled', 'Naprava omogočena')
            }}</span>
          </label>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >{{ tt('Device Name', 'Ime naprave') }} *</label
          >
          <input
            v-model="formData.deviceName"
            type="text"
            required
            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
        </div>

        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >{{ tt('Short Name', 'Kratko ime') }}
              <span class="ml-1 text-xs font-normal text-gray-400 dark:text-gray-500">{{
                tt('(compact UI, no room prefix)', '(kompaktni vmesnik, brez predpone prostora)')
              }}</span>
            </label>
            <input
              v-model="formData.shortName"
              type="text"
              :placeholder="tt('e.g. Strop', 'npr. Strop')"
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
          </div>
        </div>

        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >{{ tt('Speech Full Name', 'Polno govorno ime') }}
              <span class="ml-1 text-xs font-normal text-gray-400 dark:text-gray-500">{{
                tt('(voice assistant)', '(glasovni pomočnik)')
              }}</span>
            </label>
            <input
              v-model="formData.speechFullName"
              type="text"
              :placeholder="tt('e.g. Office Ceiling Light', 'npr. Pisarna stropna luč')"
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >{{ tt('Speech Short Name', 'Kratko govorno ime') }}
              <span class="ml-1 text-xs font-normal text-gray-400 dark:text-gray-500">{{
                tt('(voice, in room)', '(glas, v prostoru)')
              }}</span>
            </label>
            <input
              v-model="formData.speechShortName"
              type="text"
              :placeholder="tt('e.g. Ceiling', 'npr. Strop')"
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
          </div>
        </div>

        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{{
              tt('Room', 'Prostor')
            }}</label>
            <select
              v-model="formData.roomId"
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
              <option :value="null">{{ tt('No Room', 'Brez prostora') }}</option>
              <optgroup v-for="(roomsInArea, area) in roomsByArea" :key="area" :label="areaGroupLabel(area)">
                <option v-for="room in roomsInArea" :key="room.id" :value="room.id">
                  {{ room.roomName }}
                </option>
              </optgroup>
            </select>
          </div>

          <IconPicker
            v-model="formData.icon"
            :label="tt('Icon', 'Ikona')"
            fallback="devices"
            :placeholder="tt('Search icons', 'Išči ikone')" />
        </div>

        <!--
          Hardware identity — who made the thing, as opposed to what the owner
          calls it. Auto-filled at adoption from the protocol (Matter vendor,
          Zigbee manufacturer, Hue product data); editable here. This is what
          device search and the inventory list group by.

          Folded: it arrives filled in and is rarely corrected, so it costs one
          line and opens on demand.
        -->
        <details
          class="pt-3 border-t border-gray-200 dark:border-gray-700"
          :open="hardwareSectionOpen"
          @toggle="onHardwareSectionToggle">
          <summary class="flex cursor-pointer select-none items-center gap-2">
            <span class="material-symbols-outlined text-base leading-none text-gray-400">{{
              hardwareSectionOpen ? 'expand_more' : 'chevron_right'
            }}</span>
            <span class="text-sm font-semibold text-gray-900 dark:text-white">{{
              tt('Hardware', 'Strojna oprema')
            }}</span>
            <span class="ml-auto max-w-[62%] truncate text-right text-xs text-gray-400 dark:text-gray-500">{{
              hardwareSummary
            }}</span>
          </summary>
          <div class="space-y-4 pt-3">
            <div>
              <p class="text-xs text-gray-500 dark:text-gray-400">
                {{
                  tt('Brand, product and model of the physical device.', 'Znamka, izdelek in model fizične naprave.')
                }}
              </p>
            </div>

            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{{
                  tt('Brand', 'Znamka')
                }}</label>
                <input
                  v-model="formData.brandName"
                  type="text"
                  list="device-brand-suggestions"
                  :placeholder="tt('e.g. Bosch', 'npr. Bosch')"
                  class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
                <datalist id="device-brand-suggestions">
                  <option v-for="brand in brandSuggestions" :key="brand" :value="brand" />
                </datalist>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{{
                  tt('Model Number', 'Številka modela')
                }}</label>
                <input
                  v-model="formData.modelNumber"
                  type="text"
                  :placeholder="tt('e.g. BSP-FZ2', 'npr. BSP-FZ2')"
                  class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
              </div>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{{
                tt('Product Name', 'Ime izdelka')
              }}</label>
              <input
                v-model="formData.productName"
                type="text"
                :placeholder="tt('e.g. Smart Plug Compact', 'npr. Pametna vtičnica Compact')"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
            </div>

            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >{{ tt('Manual Link', 'Povezava do navodil') }}
                  <span class="ml-1 text-xs font-normal text-gray-400 dark:text-gray-500">{{
                    tt('(http/https)', '(http/https)')
                  }}</span>
                </label>
                <input
                  v-model="formData.manualUrl"
                  type="url"
                  :placeholder="tt('https://vendor.com/manual.pdf', 'https://proizvajalec.com/navodila.pdf')"
                  class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >{{ tt('Product Link', 'Povezava do izdelka') }}
                  <span class="ml-1 text-xs font-normal text-gray-400 dark:text-gray-500">{{
                    tt('(http/https)', '(http/https)')
                  }}</span>
                </label>
                <input
                  v-model="formData.productUrl"
                  type="url"
                  :placeholder="tt('https://vendor.com/product', 'https://proizvajalec.com/izdelek')"
                  class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
              </div>
            </div>
          </div>
        </details>

        <!-- Device Thumbnail -->
        <div v-if="mode === 'edit'">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{{
            tt('Device Thumbnail', 'Slika naprave')
          }}</label>
          <div
            class="flex items-center gap-3 rounded-xl p-2 -m-2 transition-colors"
            :class="
              thumbnailDragging
                ? 'bg-blue-50 dark:bg-blue-900/20 outline-2 outline-dashed outline-blue-400 dark:outline-blue-500'
                : ''
            "
            @dragenter.prevent="onThumbnailDragEnter"
            @dragover.prevent
            @dragleave="onThumbnailDragLeave"
            @drop.prevent="onThumbnailDrop">
            <div
              class="w-14 h-14 rounded-full border-2 border-gray-300 dark:border-gray-600 overflow-hidden bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
              <img
                v-if="thumbnailSrc"
                :src="thumbnailSrc"
                :alt="tt('Device thumbnail', 'Slika naprave')"
                class="w-full h-full object-cover" />
              <span v-else class="material-symbols-outlined text-gray-400 text-2xl">add_photo_alternate</span>
            </div>
            <div class="flex flex-col gap-1">
              <button
                type="button"
                @click="pickThumbnail"
                :disabled="thumbnailUploading"
                class="px-3 py-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 border border-blue-300 dark:border-blue-600 rounded-lg cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors disabled:opacity-50 disabled:cursor-default">
                {{ thumbnailUploading ? tt('Uploading...', 'Nalaganje...') : tt('Upload Image', 'Naloži sliko') }}
              </button>
              <button
                v-if="thumbnailSrc && !thumbnailUploading"
                @click="removeThumbnail"
                class="px-3 py-1.5 text-xs font-medium text-red-600 dark:text-red-400 border border-red-300 dark:border-red-600 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                {{ tt('Remove', 'Odstrani') }}
              </button>
            </div>
            <p class="text-xs text-gray-400 dark:text-gray-500 ml-1">
              {{ tt('PNG, JPEG, or WebP. Resized to 512×512.', 'PNG, JPEG ali WebP. Pomanjšano na 512×512.') }}<br />
              {{ tt('You can also drop an image here.', 'Sliko lahko tudi povlečete sem.') }}
            </p>
          </div>
        </div>

        <!-- State Image (binary sensors): shown while the sensor reads active -->
        <div v-if="mode === 'edit' && isBinarySensorDevice">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {{ tt('State Image', 'Slika stanja') }}
          </label>
          <div class="flex items-center gap-3">
            <div
              class="w-14 h-14 rounded-full border-2 border-gray-300 dark:border-gray-600 overflow-hidden bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
              <img
                v-if="stateThumbSrc"
                :src="stateThumbSrc"
                :alt="tt('State image', 'Slika stanja')"
                class="w-full h-full object-cover" />
              <span v-else class="material-symbols-outlined text-gray-400 text-2xl">add_photo_alternate</span>
            </div>
            <div class="flex flex-col gap-1">
              <button
                type="button"
                @click="pickStateImage"
                :disabled="stateThumbUploading"
                class="px-3 py-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 border border-blue-300 dark:border-blue-600 rounded-lg cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors disabled:opacity-50 disabled:cursor-default">
                {{
                  stateThumbUploading
                    ? tt('Uploading...', 'Nalaganje...')
                    : stateThumbSrc
                      ? tt('Replace Image', 'Zamenjaj sliko')
                      : tt('Upload Image', 'Naloži sliko')
                }}
              </button>
              <button
                v-if="stateThumbSrc && !stateThumbUploading"
                @click="removeStateImage"
                class="px-3 py-1.5 text-xs font-medium text-red-600 dark:text-red-400 border border-red-300 dark:border-red-600 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                {{ tt('Remove', 'Odstrani') }}
              </button>
            </div>
            <p class="text-xs text-gray-400 dark:text-gray-500 ml-1">
              {{
                tt(
                  'Shown while the sensor reads active (open, detected). The device thumbnail is the idle state.',
                  'Prikazano, ko senzor javlja aktivno stanje (odprto, zaznano). V mirovanju se prikaže slika naprave.',
                )
              }}
            </p>
          </div>
        </div>

        <!--
          The drawing pickers: the hub's class metadata decides which ones this
          device gets — a setting stamped `display: 'drawing'` — and every one is
          the same component. The device icon is no longer involved; it stays the
          glyph the rest of the app draws.

          They stand on their OWN, outside the `settingFieldKeys` gate below: that
          gate asks whether the device's settings bag already carries a key, and a
          fresh light's bag is empty — so a class whose only setting is a drawing
          (every light, whose `light_type` has never been written) used to render
          no picker at all. Each picker carries its own title, so the block needs
          no heading of its own.
        -->
        <div v-if="drawingSettings.length" class="space-y-4 pt-3 border-t border-gray-200 dark:border-gray-700">
          <DeviceVariantPicker
            v-for="setting in drawingSettings"
            :key="setting.key"
            :definition="setting.definition"
            :family="setting.family"
            :model-value="variantValue(setting)"
            @update:model-value="(value) => setVariantSetting(setting.key, value)" />
        </div>

        <!--
          The device's own 3D model (card t_6b2c5f01) — the owner's rule:
          "device ifc should be added the same way as device icon". The slot is
          the DEVICE's (`settings.model3d`), so a model attached here is drawn by
          every element that pins the device in the Home 3D view. Offered for the
          classes that have a 3D presence there — covers and lights.

          This is the ONE place a model is chosen (owner, 2026-10-30: it was also
          offered on the placement's own control settings, which was the same
          device setting asked twice).

          Folded into one row: the panel is tall and is set once, so it opens on
          demand and the summary line carries what is attached.
        -->
        <details
          v-if="showsDeviceModel"
          class="pt-3 border-t border-gray-200 dark:border-gray-700"
          :open="modelSectionOpen"
          @toggle="onModelSectionToggle">
          <summary class="flex cursor-pointer select-none items-center gap-2">
            <span class="material-symbols-outlined text-base leading-none text-gray-400">{{
              modelSectionOpen ? 'expand_more' : 'chevron_right'
            }}</span>
            <span class="text-sm font-semibold text-gray-900 dark:text-white">{{ tt('3D model', '3D model') }}</span>
            <span class="ml-auto max-w-[62%] truncate text-right text-xs text-gray-400 dark:text-gray-500">{{
              deviceModelLabel
            }}</span>
          </summary>
          <div class="pt-3">
            <DeviceModel3dConfig
              :config="deviceSettings"
              :device-id="props.device?.id ?? ''"
              heading="hide"
              @update:model="onDeviceModelEdited" />
          </div>
        </details>

        <!--
          The class's own settings — the plain fields (metering, fixture size,
          falloffs…). They follow the drawing picker and the model row above,
          because those are what an owner opens the editor to change and these
          are the detail.
        -->
        <div v-if="settingFieldKeys.length" class="space-y-4 pt-3 border-t border-gray-200 dark:border-gray-700">
          <h4 class="text-sm font-semibold text-gray-900 dark:text-white mb-3">
            {{ tt('Device settings', 'Nastavitve naprave') }}
          </h4>

          <template v-for="(value, key) in deviceSettings" :key="String(key)">
            <div
              v-if="
                key &&
                key !== 'undefined' &&
                String(key) !== DEVICE_MODEL_KEY &&
                String(key) !== RETIRED_LIGHT_KEY &&
                isSettingVisible(String(key)) &&
                !isDrawingSetting(String(key))
              ">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {{ settingDefinitions[String(key)]?.label || formatSettingLabel(String(key)) }}
              </label>
              <p
                v-if="settingDefinitions[String(key)]?.description"
                class="text-xs text-gray-500 dark:text-gray-400 mb-2">
                {{ settingDefinitions[String(key)].description }}
              </p>

              <!-- Select/Enum Input -->
              <select
                v-if="
                  settingDefinitions[String(key)]?.settingType === 'select' ||
                  settingDefinitions[String(key)]?.type === 'select'
                "
                v-model="deviceSettings[key]"
                @change="onEnumSettingChange(String(key))"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                <option
                  v-for="option in settingDefinitions[String(key)]?.options || []"
                  :key="String(option.value)"
                  :value="option.value">
                  {{ option.label }}
                </option>
              </select>

              <!-- Number Input -->
              <input
                v-else-if="settingDefinitions[String(key)]?.settingType === 'number' || typeof value === 'number'"
                v-model.number="deviceSettings[key]"
                type="number"
                :step="settingDefinitions[String(key)]?.step ?? 'any'"
                :min="settingDefinitions[String(key)]?.min ?? undefined"
                :max="settingDefinitions[String(key)]?.max ?? undefined"
                @blur="ensureNumber(key)"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />

              <!-- Value Picker Input -->
              <DeviceValuePicker
                v-else-if="settingDefinitions[String(key)]?.settingType === 'value_picker'"
                :model-value="pickedDeviceValue(deviceSettings[key])"
                :title="settingDefinitions[String(key)]?.label"
                :writable-only="settingDefinitions[String(key)]?.writableOnly"
                @update:model-value="(val) => (deviceSettings[key] = val)" />

              <!-- Boolean/Checkbox Input -->
              <input
                v-else-if="settingDefinitions[String(key)]?.settingType === 'boolean' || typeof value === 'boolean'"
                v-model="deviceSettings[key]"
                type="checkbox"
                class="w-4 h-4 text-blue-600 border-gray-300 rounded" />

              <!-- Text Input -->
              <input
                v-else
                v-model="deviceSettings[key]"
                type="text"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
            </div>
          </template>
        </div>

        <!--
          The automation categories this device belongs to. The hub derives them
          from the device class, the control type and the value mode categories —
          a pump is Climate *and* Water — and every list in the app reads that
          answer. What is set here is the owner's override: it replaces the
          derivation and lives in the device's own settings bag, so it survives
          a re-sync and needs no schema change (t_3f1d0a7c).
        -->
        <div v-if="mode === 'edit'" class="pt-3 border-t border-gray-200 dark:border-gray-700 space-y-3">
          <div class="flex items-start justify-between gap-3">
            <div>
              <h3 class="text-sm font-semibold text-gray-900 dark:text-white">
                {{ tt('Automation categories', 'Kategorije avtomatizacije') }}
              </h3>
              <p class="text-xs text-gray-500 dark:text-gray-400">
                {{
                  deviceCategoryOverride
                    ? tt(
                        'Set by hand. Automatic restores what the hub derives.',
                        'Nastavljeno ročno. Samodejno povrne, kar izpelje vozlišče.',
                      )
                    : tt(
                        'Derived from the device class and its values. A device may belong to several.',
                        'Izpeljano iz razreda naprave in njenih vrednosti. Naprava lahko pripada več.',
                      )
                }}
              </p>
            </div>
            <button
              type="button"
              class="shrink-0 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors"
              :class="
                deviceCategoryOverride
                  ? 'border-blue-300 dark:border-blue-600 text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20'
                  : 'border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500 cursor-default'
              "
              :disabled="!deviceCategoryOverride"
              @click="resetDeviceCategories">
              {{ tt('Automatic', 'Samodejno') }}
            </button>
          </div>

          <div class="flex flex-wrap gap-2">
            <label
              v-for="option in DEVICE_AUTOMATION_CATEGORIES"
              :key="option.value"
              class="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs cursor-pointer transition-colors"
              :class="
                isDeviceCategorySelected(option.value)
                  ? 'border-blue-400 dark:border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200'
                  : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/40'
              ">
              <input
                type="checkbox"
                class="w-3.5 h-3.5 text-blue-600 border-gray-300 rounded"
                :checked="isDeviceCategorySelected(option.value)"
                @change="toggleDeviceCategory(option.value)" />
              {{ tt(option.label.en, option.label.sl) }}
            </label>
          </div>
        </div>

        <!-- Endpoint Badge: shown for Matter / Camera devices that are endpoint-linked -->
        <div
          v-if="props.device?.endpoint"
          class="flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
          <span class="material-symbols-outlined text-base text-blue-600 dark:text-blue-400">hub</span>
          <div class="text-xs leading-tight">
            <span class="font-semibold text-blue-700 dark:text-blue-300 capitalize">
              {{ props.device.endpoint.endpointType }} {{ tt('endpoint', 'končna točka') }}
            </span>
            <span class="ml-2 text-blue-600 dark:text-blue-400">
              {{ props.device.endpoint.endpointLabel }}
            </span>
            <span
              v-if="!props.device.endpoint.available"
              class="ml-2 px-1 rounded text-xs bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400">
              {{ tt('offline', 'brez povezave') }}
            </span>
          </div>
        </div>
      </div>
      <!-- Values Section -->
      <div class="space-y-4">
        <!-- Recreate Form Definition (edit mode only) -->
        <div v-if="mode === 'edit' && props.device" class="flex items-center justify-end">
          <button
            @click="reinitValues"
            :disabled="reinitingValues"
            class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-amber-600 dark:text-amber-400 border border-amber-300 dark:border-amber-600 rounded-lg hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors disabled:opacity-50"
            :title="
              tt(
                'Delete and recreate device values from the device type definition',
                'Izbriši in znova ustvari vrednosti naprave po definiciji tipa naprave',
              )
            ">
            <span class="material-symbols-outlined text-sm">{{
              reinitingValues ? 'hourglass_empty' : 'restart_alt'
            }}</span>
            {{
              reinitingValues
                ? tt('Recreating...', 'Ponovno ustvarjanje...')
                : tt('Recreate Form Definition', 'Ponovno ustvari definicijo obrazca')
            }}
          </button>
        </div>
        <div v-if="deviceValuesList.length === 0" class="text-center py-12 text-gray-500 dark:text-gray-400">
          <span class="material-symbols-outlined text-6xl">data_usage</span>
          <p class="mt-2">{{ tt('No device values configured', 'Ni nastavljenih vrednosti naprave') }}</p>
          <p class="text-sm">
            {{
              tt(
                'Device values are configured when creating the device',
                'Vrednosti naprave se nastavijo ob ustvarjanju naprave',
              )
            }}
          </p>
        </div>
        <div v-else>
          <!-- <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Configure integration-specific value mappings and references
          </p> -->
          <DeviceValuesEditor
            :device-values="deviceValuesList"
            :device-id="device?.id"
            v-model:control-knx="controlKnx"
            v-model:status-knx="statusKnx"
            v-model:control-matter="controlMatter"
            v-model:status-matter="statusMatter"
            v-model:control-bacnet="controlBacnet"
            v-model:status-bacnet="statusBacnet"
            v-model:control-modbus="controlModbus"
            v-model:status-modbus="statusModbus"
            v-model:control-mqtt="controlMqtt"
            v-model:status-mqtt="statusMqtt"
            v-model:control-esphome="controlESPHome"
            v-model:status-esphome="statusESPHome"
            v-model:control-zwave="controlZWave"
            v-model:status-zwave="statusZWave"
            v-model:control-zigbee="controlZigbee"
            v-model:status-zigbee="statusZigbee"
            v-model:control-ha="controlHA"
            v-model:status-ha="statusHA"
            :integration-id="integrationId || device?.integrationId || ''"
            :integration-type="integrationType"
            :integration-mappings="templateMappings"
            :matter-endpoint-id="matterEndpointId" />
        </div>
      </div>

      <!-- Edit Mode Sections (shown below the main sections) -->

      <!-- Custom Values Section (edit mode only) -->
      <div v-if="mode === 'edit' && props.device" class="border-t border-gray-200 dark:border-gray-700 pt-6 mt-2">
        <div class="flex items-center justify-between mb-4">
          <div>
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
              {{ tt('Custom Values', 'Vrednosti po meri') }}
            </h3>
            <p class="text-sm text-gray-500 dark:text-gray-400">
              {{
                tt(
                  'Add values linked to other endpoints, entities or datapoints',
                  'Dodajte vrednosti, povezane z drugimi končnimi točkami, entitetami ali podatkovnimi točkami',
                )
              }}
            </p>
          </div>
          <button
            @click="
              showAddCustomValue = true;
              showNewCvLinkSection = false;
            "
            class="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center gap-1">
            <span class="material-symbols-outlined text-sm">add</span>
            {{ tt('Add Value', 'Dodaj vrednost') }}
          </button>
        </div>

        <!-- Existing custom values (expandable rows) -->
        <div v-if="customValues.length > 0" class="space-y-2 mb-4">
          <div
            v-for="cv in customValues"
            :key="cv.id"
            class="rounded-lg border bg-white dark:bg-gray-800"
            :class="
              expandedCustomValueIds.has(cv.id)
                ? 'border-blue-300 dark:border-blue-700'
                : 'border-gray-200 dark:border-gray-700'
            ">
            <!-- Row header -->
            <div class="flex items-center justify-between p-3">
              <div class="flex items-center gap-3 min-w-0">
                <span class="material-symbols-outlined text-gray-400">data_usage</span>
                <div class="min-w-0">
                  <div class="flex items-center gap-2">
                    <span class="text-sm font-medium text-gray-900 dark:text-white">{{ cv.name }}</span>
                    <!-- Protocol badges when linked -->
                    <span
                      v-if="cv.statusReference"
                      class="px-1.5 py-0.5 text-xs rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300"
                      :title="tt('Status', 'Stanje') + ': ' + cvRefProtocolLabel(cv.statusReference)">
                      {{ cvRefProtocolLabel(cv.statusReference) }}
                    </span>
                    <span
                      v-if="cv.controlReference"
                      class="px-1.5 py-0.5 text-xs rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"
                      :title="tt('Control', 'Upravljanje') + ': ' + cvRefProtocolLabel(cv.controlReference)">
                      {{ cvRefProtocolLabel(cv.controlReference) }}
                    </span>
                  </div>
                  <div class="text-xs text-gray-500 dark:text-gray-400">
                    {{ cv.valueKey }} · {{ cv.valueType }}{{ cv.unit ? ` · ${cv.unit}` : '' }}
                    <span v-if="cv.currentValue !== null && cv.currentValue !== undefined" class="ml-2 font-mono"
                      >= {{ cv.currentValue }}</span
                    >
                  </div>
                </div>
              </div>
              <div class="flex items-center gap-1">
                <button
                  @click="toggleCvExpand(cv.id)"
                  class="p-1.5 rounded transition-colors"
                  :class="
                    expandedCustomValueIds.has(cv.id)
                      ? 'text-blue-600 bg-blue-100 dark:bg-blue-900/30'
                      : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20'
                  "
                  :title="
                    expandedCustomValueIds.has(cv.id) ? tt('Collapse', 'Strni') : tt('Link to source', 'Poveži z virom')
                  ">
                  <span class="material-symbols-outlined text-sm">{{
                    expandedCustomValueIds.has(cv.id) ? 'expand_less' : 'link'
                  }}</span>
                </button>
                <button
                  @click="deleteCustomValue(cv)"
                  class="p-1.5 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-colors"
                  :title="tt('Delete custom value', 'Izbriši vrednost po meri')">
                  <span class="material-symbols-outlined text-sm">delete</span>
                </button>
              </div>
            </div>

            <!-- Expanded: reference editor -->
            <div
              v-if="expandedCustomValueIds.has(cv.id)"
              class="border-t border-blue-200 dark:border-blue-700 p-4 space-y-4 bg-blue-50/50 dark:bg-blue-900/5">
              <!-- Status reference -->
              <div class="space-y-2">
                <div class="flex items-center gap-2">
                  <span class="material-symbols-outlined text-sm text-gray-500">sensors</span>
                  <span class="text-xs font-semibold text-gray-700 dark:text-gray-300">{{
                    tt('Status source', 'Vir stanja')
                  }}</span>
                  <span class="text-xs text-gray-400">{{ tt('(read value from)', '(branje vrednosti iz)') }}</span>
                </div>
                <!-- KNX -->
                <KNXReferenceInput
                  v-if="integrationType === 'knx'"
                  :model-value="asKnxConfig(cvStatusRefs[cv.id])"
                  @update:model-value="(v) => (cvStatusRefs[cv.id] = v ?? null)"
                  :mapping="undefined"
                  :integration-id="props.device.integrationId || integrationId || ''" />
                <!-- HA -->
                <HAEntityReferenceInput
                  v-else-if="integrationType === 'homeassistant'"
                  :model-value="asHaRef(cvStatusRefs[cv.id])"
                  @update:model-value="(v) => (cvStatusRefs[cv.id] = v ?? null)"
                  :integration-id="props.device.integrationId || integrationId || ''"
                  reference-type="status"
                  :value-type="cv.valueType" />
                <ESPHomeReferenceInput
                  v-else-if="integrationType === 'esphome'"
                  :model-value="asEspHomeRef(cvStatusRefs[cv.id])"
                  @update:model-value="(v) => (cvStatusRefs[cv.id] = v ?? null)"
                  :integration-id="props.device.integrationId || integrationId || ''"
                  reference-type="status" />
                <!-- Matter -->
                <MatterReferenceInput
                  v-else-if="integrationType === 'matter'"
                  :model-value="asMatterConfig(cvStatusRefs[cv.id])"
                  @update:model-value="(v) => (cvStatusRefs[cv.id] = v ?? null)"
                  :mapping="undefined"
                  :integration-id="props.device.integrationId || integrationId || ''"
                  :matter-endpoint-id="matterEndpointId" />
                <!-- Z-Wave -->
                <ZWaveReferenceInput
                  v-else-if="integrationType === 'zwave'"
                  :model-value="asZWaveRef(cvStatusRefs[cv.id])"
                  @update:model-value="(v) => (cvStatusRefs[cv.id] = v ?? null)"
                  :integration-id="props.device.integrationId || integrationId || ''"
                  reference-type="status" />
                <!-- Zigbee -->
                <ZigbeeReferenceInput
                  v-else-if="integrationType === 'zigbee'"
                  :model-value="asZigbeeRef(cvStatusRefs[cv.id])"
                  @update:model-value="(v) => (cvStatusRefs[cv.id] = v ?? null)"
                  :integration-id="props.device.integrationId || integrationId || ''"
                  reference-type="status" />
              </div>

              <!-- Control reference (only if settable) -->
              <div v-if="cv.modeSettable" class="space-y-2">
                <div class="flex items-center gap-2">
                  <span class="material-symbols-outlined text-sm text-gray-500">send</span>
                  <span class="text-xs font-semibold text-gray-700 dark:text-gray-300">{{
                    tt('Control target', 'Cilj upravljanja')
                  }}</span>
                  <span class="text-xs text-gray-400">{{ tt('(send command to)', '(pošiljanje ukaza v)') }}</span>
                </div>
                <KNXReferenceInput
                  v-if="integrationType === 'knx'"
                  :model-value="asKnxConfig(cvControlRefs[cv.id])"
                  @update:model-value="(v) => (cvControlRefs[cv.id] = v ?? null)"
                  :mapping="undefined"
                  :integration-id="props.device.integrationId || integrationId || ''" />
                <HAEntityReferenceInput
                  v-else-if="integrationType === 'homeassistant'"
                  :model-value="asHaRef(cvControlRefs[cv.id])"
                  @update:model-value="(v) => (cvControlRefs[cv.id] = v ?? null)"
                  :integration-id="props.device.integrationId || integrationId || ''"
                  reference-type="control"
                  :value-type="cv.valueType" />
                <ESPHomeReferenceInput
                  v-else-if="integrationType === 'esphome'"
                  :model-value="asEspHomeRef(cvControlRefs[cv.id])"
                  @update:model-value="(v) => (cvControlRefs[cv.id] = v ?? null)"
                  :integration-id="props.device.integrationId || integrationId || ''"
                  reference-type="control" />
                <MatterReferenceInput
                  v-else-if="integrationType === 'matter'"
                  :model-value="asMatterConfig(cvControlRefs[cv.id])"
                  @update:model-value="(v) => (cvControlRefs[cv.id] = v ?? null)"
                  :mapping="undefined"
                  :integration-id="props.device.integrationId || integrationId || ''"
                  :matter-endpoint-id="matterEndpointId" />
                <ZWaveReferenceInput
                  v-else-if="integrationType === 'zwave'"
                  :model-value="asZWaveRef(cvControlRefs[cv.id])"
                  @update:model-value="(v) => (cvControlRefs[cv.id] = v ?? null)"
                  :integration-id="props.device.integrationId || integrationId || ''"
                  reference-type="control" />
                <ZigbeeReferenceInput
                  v-else-if="integrationType === 'zigbee'"
                  :model-value="asZigbeeRef(cvControlRefs[cv.id])"
                  @update:model-value="(v) => (cvControlRefs[cv.id] = v ?? null)"
                  :integration-id="props.device.integrationId || integrationId || ''"
                  reference-type="control" />
              </div>

              <!-- Transformer config (for status) -->
              <TransformerConfigInput
                v-if="
                  cvStatusRefs[cv.id] &&
                  (cv.valueType === 'number' || cv.valueType === 'boolean' || cv.valueType === 'enum')
                "
                v-model="cvTransformerConfigs[cv.id]"
                :value-type="cv.valueType" />

              <!-- Save button -->
              <div class="flex items-center gap-2 pt-1">
                <button
                  @click="saveCvReferences(cv)"
                  :disabled="savingCvRefId === cv.id"
                  class="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg transition-colors flex items-center gap-1">
                  <span v-if="savingCvRefId === cv.id" class="material-symbols-outlined text-sm animate-spin"
                    >progress_activity</span
                  >
                  {{ savingCvRefId === cv.id ? tt('Saving…', 'Shranjevanje…') : tt('Save Links', 'Shrani povezave') }}
                </button>
                <button
                  @click="
                    () => {
                      const s = new Set(expandedCustomValueIds);
                      s.delete(cv.id);
                      expandedCustomValueIds = s;
                    }
                  "
                  class="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
                  {{ tt('Cancel', 'Prekliči') }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- No custom values -->
        <div v-else class="text-center py-6 text-gray-400 dark:text-gray-500 text-sm">
          {{ tt('No custom values added yet', 'Ni dodanih vrednosti po meri') }}
        </div>

        <!-- Add custom value form (inline) -->
        <div
          v-if="showAddCustomValue"
          class="p-4 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/10 space-y-3">
          <h4 class="text-sm font-semibold text-gray-900 dark:text-white">
            {{ tt('New Custom Value', 'Nova vrednost po meri') }}
          </h4>
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1"
                >{{ tt('Value Key', 'Ključ vrednosti') }} *</label
              >
              <input
                v-model="newCustomValue.valueKey"
                type="text"
                :placeholder="tt('e.g. power_consumption', 'npr. power_consumption')"
                class="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
            </div>
            <div>
              <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1"
                >{{ tt('Display Name', 'Prikazno ime') }} *</label
              >
              <input
                v-model="newCustomValue.name"
                type="text"
                :placeholder="tt('e.g. Power Consumption', 'npr. Poraba energije')"
                class="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
            </div>
            <div>
              <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1"
                >{{ tt('Value Type', 'Tip vrednosti') }} *</label
              >
              <select
                v-model="newCustomValue.valueType"
                class="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                <option value="number">{{ tt('Number', 'Število') }}</option>
                <option value="boolean">{{ tt('Boolean', 'Logična vrednost') }}</option>
                <option value="string">{{ tt('String', 'Besedilo') }}</option>
                <option value="enum">{{ tt('Enum', 'Naštevilka') }}</option>
              </select>
            </div>
            <div>
              <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">{{
                tt('Unit', 'Enota')
              }}</label>
              <input
                v-model="newCustomValue.unit"
                type="text"
                :placeholder="tt('e.g. W, %, °C', 'npr. W, %, °C')"
                class="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
            </div>
          </div>
          <div class="flex items-center gap-2">
            <label class="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                v-model="newCustomValue.settable"
                class="w-4 h-4 text-blue-600 border-gray-300 rounded" />
              {{ tt('Settable (writable from UI)', 'Nastavljivo (pisanje iz vmesnika)') }}
            </label>
          </div>

          <!-- Link to source (optional) -->
          <div class="border-t border-blue-200 dark:border-blue-700 pt-3">
            <button
              class="flex items-center gap-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
              @click="showNewCvLinkSection = !showNewCvLinkSection">
              <span class="material-symbols-outlined text-sm">{{ showNewCvLinkSection ? 'expand_less' : 'link' }}</span>
              {{
                showNewCvLinkSection
                  ? tt('Hide source link', 'Skrij povezavo vira')
                  : tt('Link to source endpoint (optional)', 'Poveži s končno točko vira (neobvezno)')
              }}
            </button>

            <div v-if="showNewCvLinkSection" class="mt-3 space-y-4">
              <!-- Status reference -->
              <div class="space-y-2">
                <div class="flex items-center gap-2">
                  <span class="material-symbols-outlined text-sm text-gray-500">sensors</span>
                  <span class="text-xs font-semibold text-gray-700 dark:text-gray-300">{{
                    tt('Status source', 'Vir stanja')
                  }}</span>
                  <span class="text-xs text-gray-400">{{ tt('(read from)', '(branje iz)') }}</span>
                </div>
                <KNXReferenceInput
                  v-if="integrationType === 'knx'"
                  :model-value="asKnxConfig(newCvStatusRef)"
                  @update:model-value="(v) => (newCvStatusRef = v ?? null)"
                  :mapping="undefined"
                  :integration-id="integrationId || props.device?.integrationId || ''" />
                <HAEntityReferenceInput
                  v-else-if="integrationType === 'homeassistant'"
                  :model-value="asHaRef(newCvStatusRef)"
                  @update:model-value="(v) => (newCvStatusRef = v ?? null)"
                  :integration-id="integrationId || props.device?.integrationId || ''"
                  reference-type="status"
                  :value-type="newCustomValue.valueType" />
                <ESPHomeReferenceInput
                  v-else-if="integrationType === 'esphome'"
                  :model-value="asEspHomeRef(newCvStatusRef)"
                  @update:model-value="(v) => (newCvStatusRef = v ?? null)"
                  :integration-id="integrationId || props.device?.integrationId || ''"
                  reference-type="status" />
                <MatterReferenceInput
                  v-else-if="integrationType === 'matter'"
                  :model-value="asMatterConfig(newCvStatusRef)"
                  @update:model-value="(v) => (newCvStatusRef = v ?? null)"
                  :mapping="undefined"
                  :integration-id="integrationId || props.device?.integrationId || ''"
                  :matter-endpoint-id="matterEndpointId" />
                <ZWaveReferenceInput
                  v-else-if="integrationType === 'zwave'"
                  :model-value="asZWaveRef(newCvStatusRef)"
                  @update:model-value="(v) => (newCvStatusRef = v ?? null)"
                  :integration-id="integrationId || props.device?.integrationId || ''"
                  reference-type="status" />
                <ZigbeeReferenceInput
                  v-else-if="integrationType === 'zigbee'"
                  :model-value="asZigbeeRef(newCvStatusRef)"
                  @update:model-value="(v) => (newCvStatusRef = v ?? null)"
                  :integration-id="integrationId || props.device?.integrationId || ''"
                  reference-type="status" />
              </div>

              <!-- Control reference (only if settable) -->
              <div v-if="newCustomValue.settable" class="space-y-2">
                <div class="flex items-center gap-2">
                  <span class="material-symbols-outlined text-sm text-gray-500">send</span>
                  <span class="text-xs font-semibold text-gray-700 dark:text-gray-300">{{
                    tt('Control target', 'Cilj upravljanja')
                  }}</span>
                  <span class="text-xs text-gray-400">{{ tt('(send command to)', '(pošiljanje ukaza v)') }}</span>
                </div>
                <KNXReferenceInput
                  v-if="integrationType === 'knx'"
                  :model-value="asKnxConfig(newCvControlRef)"
                  @update:model-value="(v) => (newCvControlRef = v ?? null)"
                  :mapping="undefined"
                  :integration-id="integrationId || props.device?.integrationId || ''" />
                <HAEntityReferenceInput
                  v-else-if="integrationType === 'homeassistant'"
                  :model-value="asHaRef(newCvControlRef)"
                  @update:model-value="(v) => (newCvControlRef = v ?? null)"
                  :integration-id="integrationId || props.device?.integrationId || ''"
                  reference-type="control"
                  :value-type="newCustomValue.valueType" />
                <ESPHomeReferenceInput
                  v-else-if="integrationType === 'esphome'"
                  :model-value="asEspHomeRef(newCvControlRef)"
                  @update:model-value="(v) => (newCvControlRef = v ?? null)"
                  :integration-id="integrationId || props.device?.integrationId || ''"
                  reference-type="control" />
                <MatterReferenceInput
                  v-else-if="integrationType === 'matter'"
                  :model-value="asMatterConfig(newCvControlRef)"
                  @update:model-value="(v) => (newCvControlRef = v ?? null)"
                  :mapping="undefined"
                  :integration-id="integrationId || props.device?.integrationId || ''"
                  :matter-endpoint-id="matterEndpointId" />
                <ZWaveReferenceInput
                  v-else-if="integrationType === 'zwave'"
                  :model-value="asZWaveRef(newCvControlRef)"
                  @update:model-value="(v) => (newCvControlRef = v ?? null)"
                  :integration-id="integrationId || props.device?.integrationId || ''"
                  reference-type="control" />
                <ZigbeeReferenceInput
                  v-else-if="integrationType === 'zigbee'"
                  :model-value="asZigbeeRef(newCvControlRef)"
                  @update:model-value="(v) => (newCvControlRef = v ?? null)"
                  :integration-id="integrationId || props.device?.integrationId || ''"
                  reference-type="control" />
              </div>

              <!-- Transformer config -->
              <TransformerConfigInput
                v-if="
                  newCvStatusRef &&
                  (newCustomValue.valueType === 'number' ||
                    newCustomValue.valueType === 'boolean' ||
                    newCustomValue.valueType === 'enum')
                "
                v-model="newCvTransformerConfig"
                :value-type="newCustomValue.valueType" />
            </div>
          </div>

          <div class="flex items-center gap-2 pt-1">
            <button
              @click="addCustomValue"
              :disabled="!newCustomValue.valueKey.trim() || !newCustomValue.name.trim() || addingCustomValue"
              class="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg transition-colors">
              {{ addingCustomValue ? tt('Adding...', 'Dodajanje...') : tt('Add', 'Dodaj') }}
            </button>
            <button
              @click="
                showAddCustomValue = false;
                showNewCvLinkSection = false;
                newCvStatusRef = null;
                newCvControlRef = null;
              "
              class="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
              {{ tt('Cancel', 'Prekliči') }}
            </button>
          </div>
        </div>
      </div>

      <!-- Automations Section (edit mode only) -->
      <div v-if="mode === 'edit' && props.device" class="border-t border-gray-200 dark:border-gray-700 pt-6 mt-2">
        <DeviceAutomationsTab :device-id="props.device.id" :device-type="props.device.deviceType || ''" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { apiFetch, apiFetchRaw } from '../services/api';
import { getDefaultThumbnailUrl, getStateThumbnailUrl } from '../composables/useDeviceThumbnail';
import { useImageAssetsStore } from '../stores/imageAssets';
import { firstImageFromDrop, pickImageFile } from '../services/imageFilePicker';
import { useSocketStore } from '../stores/socket';
import { useDeviceClassesStore } from '../stores/deviceClasses';
import type { DeviceClass, DeviceClassMetadata, DeviceClassSetting } from '../stores/deviceClasses';
import { useEntitiesStore } from '../stores/entities';
import DeviceValuesEditor from './DeviceValuesEditor.vue';
import DeviceAutomationsTab from './DeviceAutomationsTab.vue';
import DeviceValuePicker from './DeviceValuePicker.vue';
import KNXReferenceInput, { type KNXConfig } from './KNXReferenceInput.vue';
import MatterReferenceInput, { type MatterConfig } from './MatterReferenceInput.vue';
import HAEntityReferenceInput, { type HAEntityRef } from './HAEntityReferenceInput.vue';
import ESPHomeReferenceInput, { type ESPHomeEntityRef } from './ESPHomeReferenceInput.vue';
import ZWaveReferenceInput, { type ZWaveValueRef } from './ZWaveReferenceInput.vue';
import ZigbeeReferenceInput, { type ZigbeeAttributeRef } from './ZigbeeReferenceInput.vue';
import TransformerConfigInput, { type TransformerConfig } from './TransformerConfigInput.vue';
import type { BacnetConfig } from './BacnetReferenceInput.vue';
import type { ModbusConfig } from './ModbusReferenceInput.vue';
import type { MQTTConfig } from './MQTTReferenceInput.vue';
import IconPicker from './inputs/IconPicker.vue';
import { tt } from '../utils/consoleText';
import { AUTOMATION_CATEGORY_OPTIONS } from '../utils/automationCategories';
import type { PropertyMapping } from '../shared/propertyMapping';
import type { Device, DeviceValueRow, Room } from '@/types';
import { isWireObject, wireJsonBag, wireJsonValue, wireRow, type WireJsonBag } from '../utils/wire';
import { useI18n } from 'vue-i18n';
import { useToast } from '../composables/useToast';
import { useMessageBox } from '../composables/useMessageBox';
import DeviceVariantPicker from './device-editor/DeviceVariantPicker.vue';
import { variantByDrawing, variantBySettingKey } from './device-editor/variants/registry';
import type { VariantFamily } from './device-editor/variants/types';
import DeviceModel3dConfig from './dashboard/config/DeviceModel3dConfig.vue';
import { DEVICE_MODEL_KEY, deviceModelJson, parseDeviceModel, type DeviceModel3d } from '@/utils/bimDeviceModel';
import { LUMINAIRE_VARIANT_TITLES } from '@/utils/bimLuminaireParts';
import { isShadeDevice } from '@/utils/shadeDevices';
import { isLightDevice } from '../stores/lights';
import { shouldSendReferencePayload } from '@/utils/deviceValueReferences';

const { t } = useI18n();
const toast = useToast();
const { confirm: confirmBox } = useMessageBox();
/**
 * The editor's own type vocabulary (card t_f4977822). Every name below is a shape
 * the hub's JSON already carries; each is declared once because the same shape is
 * read at a dozen sites.
 */

/**
 * One protocol reference as the pickers hand it back: a KNX address, a Matter
 * property, an HA entity, … A row carries one protocol's fields, so every field
 * is optional and `buildRefPayload` duck-types the protocol from what is set.
 */
interface ProtocolRef {
  // KNX
  groupAddress?: string;
  dpt?: number;
  // Matter
  propertyId?: string;
  clusterId?: number;
  attributeId?: number;
  // Home Assistant
  haEntityId?: string;
  entityId?: string;
  domain?: string;
  friendlyName?: string;
  haField?: string;
  haStateTrue?: string;
  haStateFalse?: string;
  scaleFactor?: number;
  valueOffset?: number;
  haDomain?: string;
  haService?: string;
  haServiceOn?: string;
  haServiceOff?: string;
  haServiceParam?: string;
  // ESPHome
  esphomeEntityId?: string;
  entityKey?: string;
  entityType?: string;
  field?: string;
  // Z-Wave
  zwaveValueId?: string;
  zwaveEndpointId?: string;
  propertyName?: string;
  ccId?: number;
  ccName?: string;
  // Zigbee
  zigbeeAttributeId?: string;
  zigbeeEndpointId?: string;
  attributeName?: string;
  clusterName?: string;
  // What the hub's reference rows carry alongside the protocol fields.
  propertyType?: string;
  protocolKey?: string;
  transformerConfig?: StoredTransformerConfig;
}

/**
 * The transformer config as the hub stores it: the UI's own `TransformerConfig`
 * plus the keys the HA/ESPHome payloads carry (`entityId`, `domain`, …) and the
 * legacy pair the ESPHome path renames (`trueValue`/`falseValue`).
 */
interface StoredTransformerConfig extends TransformerConfig {
  entityId?: string;
  domain?: string;
  haField?: string;
  haDomain?: string;
  haService?: string;
  haServiceOn?: string;
  haServiceOff?: string;
  haServiceParam?: string;
  field?: string;
  trueValue?: string;
  falseValue?: string;
  /** KNX stores the group address it writes to inside the transformer config. */
  groupAddress?: string;
}

/**
 * One reference's stored transformer config, read off the hub's JSON column
 * (`DeviceValueReference.transformerConfig`) into the editor's own shape: the
 * bag is trusted once it is known to be an object — the same assertion
 * `wireRow` states once for a hub payload (card t_9d7cfe72).
 */
function transformerConfigOf(value: unknown): StoredTransformerConfig | undefined {
  if (!isWireObject(value)) return undefined;
  return wireRow<StoredTransformerConfig>(value, {});
}

/** A reference row as the hub's `/values/:id/references` endpoint takes it. */
interface ReferencePayload extends ProtocolRef {
  propertyType: string;
  transformerConfig?: StoredTransformerConfig;
}

/** The body the two reference saves hand the hub. */
interface ReferenceUpdate {
  status?: ReferencePayload | null;
  control?: ReferencePayload | null;
}

/** A KNX row of the hub's device JSON, as the reads below take it. */
interface KnxDptRow {
  dptType?: number;
  dpt?: number;
}

/** The picked-value row the value picker's settings hold. */
interface PickedValueRow {
  valueId: string;
  deviceName?: string;
  propertyName?: string;
}

/** A custom value row: the device's own value, so it carries the hub id the
 *  reference maps and the reference saves are keyed by. */
type CustomValue = DeviceValueRow & { id: string };

/** The form as the editor holds it; `getData()` spreads it into the payload. */
interface DeviceFormData {
  deviceName: string;
  shortName: string | null;
  speechFullName: string | null;
  speechShortName: string | null;
  /** Maker of the physical hardware (Bosch), its product line and model/SKU,
   *  plus the manual and product page. Auto-filled from the protocol at
   *  adoption; see `services/deviceIdentity` on the hub. */
  brandName: string | null;
  productName: string | null;
  modelNumber: string | null;
  manualUrl: string | null;
  productUrl: string | null;
  integrationId: string | null;
  roomId: string | null;
  deviceType: string | null;
  icon: string;
  enabled: boolean;
  config: WireJsonBag;
  settings: Record<string, unknown>;
  logicScript: string;
  templateId?: string | null;
}

/** One row of `getData()`'s `datapoints`: the class value it belongs to, the
 *  protocol it is for, and that protocol's own config. */
interface DatapointPayload {
  property_key?: string;
  config: Record<string, unknown>;
  propertyType?: string;
  propertyId?: string;
  zwaveValueId?: string;
  zigbeeAttributeId?: string;
  haEntityId?: string;
  entityKey?: string;
}

/** What `getData()` hands the parent: the form plus the class and datapoints. */
interface DevicePayload extends DeviceFormData {
  classId?: string;
  datapoints?: DatapointPayload[];
}

/** The socket's `value_changed` payload (`stores/socket`). */
interface ValueChangedPayload {
  deviceId?: string;
  valueId?: string;
  valueKey?: string;
  value?: unknown;
  /** The hub sends it as an ISO string; it is stored as the model's date. */
  timestamp?: Date;
}

interface Props {
  mode: 'add' | 'edit';
  device?: Device;
  integrationId?: string;
  initialRoomId?: string | null;
  allowEmptyReferences?: boolean;
}

const props = defineProps<Props>();

defineEmits<{
  save: [device: Device];
  cancel: [];
  delete: [];
}>();

const deviceClassesStore = useDeviceClassesStore();
const socketStore = useSocketStore();
const entitiesStore = useEntitiesStore();

// State
const loading = ref(false);
const selectedClassId = ref<string | null>(props.device?.deviceType || null);
const selectedClass = ref<DeviceClass | null>(null);
const availableClasses = ref<DeviceClassMetadata[]>([]);
const integrationType = ref<string>('');

const thumbnailPreview = ref<string | null>(null);
const thumbnailDragging = ref(false);
const thumbnailUploading = ref(false);
const thumbnailRemoved = ref(false);

// State image (binary sensors): the picture shown while the sensor reads active
const stateThumbPreview = ref<string | null>(null);
const stateThumbUploading = ref(false);
const stateThumbRemoved = ref(false);

/**
 * The picture to show next to the upload buttons.
 *
 * `thumbnailPreview` is a blob URL for the thumbnail just saved; otherwise the
 * device's own thumbnail is read through the image-asset cache, because a raw
 * `device.thumbnailUrl` is a hub-side presigned URL that Electron's cloud /
 * WebRTC surface cannot reach — that is why a freshly uploaded picture used to
 * stay blank here while the dashboard widget (which uses the cache) showed it.
 */
const thumbnailSrc = computed(() => {
  if (thumbnailPreview.value) return thumbnailPreview.value;
  if (thumbnailRemoved.value || !props.device) return null;
  return getDefaultThumbnailUrl(props.device);
});

/** The state image's preview, read through the same blob cache. */
const stateThumbSrc = computed(() => {
  if (stateThumbPreview.value) return stateThumbPreview.value;
  if (stateThumbRemoved.value || !props.device) return null;
  return getStateThumbnailUrl(props.device);
});

// Device type helpers
const deviceControlType = computed(() =>
  (props.device?.deviceControl || props.device?.controlType || '').toLowerCase(),
);
const deviceType = computed(() => (props.device?.deviceType || '').toLowerCase());
/**
 * A binary sensor — its own settings and its state thumbnail hang off this.
 * (Its *drawing* is no longer a bespoke row: the hub declares
 * `binary_sensor_subtype` as a drawing setting like every other family.)
 */
const isBinarySensorDevice = computed(
  () => deviceControlType.value.includes('binary_sensor') || deviceType.value === 'sensor',
);

/**
 * The drawing settings this device's class declares.
 *
 * The class metadata is the one source: a setting stamped `display: 'drawing'`
 * names its family in `drawing`, and the registry supplies the wording and the
 * resolver. Nothing here knows about covers or pumps by name.
 */
function isDrawingSetting(key: string): boolean {
  const definition = settingDefinitions.value[key];
  return Boolean(definition && (definition.display === 'drawing' || definition.settingType === 'drawing'));
}

const drawingSettings = computed(() =>
  Object.entries(settingDefinitions.value)
    .filter(([key]) => isDrawingSetting(key))
    .map(([key, definition]) => ({
      key,
      definition,
      family: variantByDrawing(definition.drawing) ?? variantBySettingKey(key),
    }))
    .filter(
      (
        setting,
      ): setting is {
        key: string;
        definition: (typeof settingDefinitions.value)[string];
        family: NonNullable<ReturnType<typeof variantByDrawing>>;
      } => setting.family !== null,
    )
    .sort((a, b) => Number(a.definition.sortOrder ?? 0) - Number(b.definition.sortOrder ?? 0)),
);

/** A pick writes the setting — and, for an appliance, the hub's load name. */
function setVariantSetting(key: string, value: string) {
  deviceSettings.value[key] = value;
  const family = variantBySettingKey(key);
  const option = family?.options.find((entry) => entry.value === value);
  if (family?.drawing === 'appliance' && option?.load) {
    deviceSettings.value.device_name = option.load;
  }
}

/**
 * The values the class itself offers. The hub validates a `select` value
 * against its own options on save, so seeding must never write anything else.
 */
function offeredVariants(definition: DeviceClassSetting): string[] {
  return (definition.options ?? []).map((option) => String(option.value ?? ''));
}

/**
 * The chip to paint as chosen. A device saved before its setting existed has no
 * value yet, so show the kind its icon and name already resolve to — otherwise
 * the row would look unset while the tile draws, say, a circulator.
 */
function variantValue(setting: { key: string; family: VariantFamily; definition: DeviceClassSetting }): string {
  const offered = offeredVariants(setting.definition);
  const allowed = (value: string) => offered.length === 0 || offered.includes(value);

  const stored = deviceSettings.value[setting.key];
  if (typeof stored === 'string' && stored !== '' && allowed(stored)) return stored;

  const resolved = setting.family.resolve({
    settings: deviceSettings.value,
    icon: formData.value.icon,
    deviceName: formData.value.deviceName,
    deviceType: deviceType.value,
    controlType: deviceControlType.value,
  });
  return allowed(resolved) ? resolved : setting.family.defaultValue;
}

/**
 * Devices saved before the setting existed kept their kind in the icon (and the
 * name). Adopt it on save, so the choice stops depending on a glyph — without
 * touching the icon, which stays whatever glyph the device carried.
 */
function seedDrawingSettings() {
  for (const setting of drawingSettings.value) {
    const current = deviceSettings.value[setting.key];
    if (current !== undefined && current !== null && current !== '') continue;
    const resolved = setting.family.resolve({
      settings: deviceSettings.value,
      icon: formData.value.icon,
      deviceName: formData.value.deviceName,
      deviceType: deviceType.value,
      controlType: deviceControlType.value,
    });
    // A class that offers fewer vessels than the family draws (the waste plant
    // offers two) keeps what the icon already drew and stores nothing, rather
    // than a value the hub would refuse on save.
    const offered = offeredVariants(setting.definition);
    if (offered.length > 0 && !offered.includes(resolved)) continue;
    deviceSettings.value[setting.key] = resolved;
  }
}

const isCoverDevice = computed(() => {
  const ctrl = deviceControlType.value;
  const dt = deviceType.value;
  return (
    ctrl.includes('cover') ||
    ctrl.includes('blind') ||
    ctrl.includes('shade') ||
    dt.includes('cover') ||
    dt.includes('blind')
  );
});

/**
 * A device that already carries a 3D model — the slot its settings hold.
 *
 * The section is offered for these whatever their class, so an attach made
 * before a re-class (or on a device the matchers below do not name) stays
 * reachable and removable.
 */
const deviceHasModel = computed(() => parseDeviceModel(deviceSettings.value) !== null);

/**
 * Whether this device's class is one the 3D-model section is offered to: covers
 * and lights — the classes whose 3D presence the Home 3D view can draw (card
 * t_6b2c5f01). The two matchers are the ones the shade and light surfaces list
 * their devices with (`utils/shadeDevices.isShadeDevice`, `stores/lights.isLightDevice`),
 * plus the editor's own cover rule for the controls those word matchers do not
 * carry; when in doubt it shows, because a cover or light word anywhere in the
 * class means the device belongs on the house's geometry.
 */
const isModelableDevice = computed(() => {
  const device = props.device;
  if (!device) return false; // add mode: no device row, and no icon/asset slot to hang it on
  return isShadeDevice(device) || isLightDevice(device) || isCoverDevice.value;
});

/**
 * The 3D-model section, offered in EDIT mode only: the model is device state
 * (settings + the device's own IFC slot), and both need the device row to exist.
 */
const showsDeviceModel = computed(() => props.mode === 'edit' && (isModelableDevice.value || deviceHasModel.value));

/**
 * The device-light bag of the retired split (`settings.light`): a lamp's light
 * used to be configurable on its own, apart from its 3D model, and a device set
 * up then still carries the key. It is not a setting of its class, so it must
 * never render as one of the Device Settings rows.
 */
const RETIRED_LIGHT_KEY = 'light';

/**
 * The folded 3D-model row: shut until asked for, because the panel is tall and
 * is set once per device. The toggle keeps the ref in step with the native
 * `<details>`, so the chevron and the element agree.
 */
const modelSectionOpen = ref(false);

function onModelSectionToggle(event: Event): void {
  modelSectionOpen.value = (event.target as HTMLDetailsElement).open;
}

/** The hardware row, folded the same way: filled at adoption, rarely corrected. */
const hardwareSectionOpen = ref(false);

function onHardwareSectionToggle(event: Event): void {
  hardwareSectionOpen.value = (event.target as HTMLDetailsElement).open;
}

/** The folded hardware row's one line: what the hub filled in, or nothing. */
const hardwareSummary = computed(() =>
  [formData.value.brandName, formData.value.modelNumber, formData.value.productName]
    .map((part) => String(part ?? '').trim())
    .filter(Boolean)
    .join(' · '),
);

/** What the folded row says is attached, in one line. */
const deviceModelLabel = computed(() => {
  const model = parseDeviceModel(deviceSettings.value);
  if (!model) return tt('None — the device draws as its symbol', 'Brez — naprava se riše kot simbol');
  if (model.source === 'ifc') return model.ifc?.name || tt('Imported IFC', 'Uvožen IFC');
  const variant = model.library?.prefs?.variant as keyof typeof LUMINAIRE_VARIANT_TITLES | undefined;
  const title = variant ? LUMINAIRE_VARIANT_TITLES[variant] : undefined;
  return title
    ? tt(title.en, title.sl)
    : [model.library?.type ?? '', model.library?.model ?? ''].filter(Boolean).join(' · ');
});

/**
 * The settings the Device Settings list renders — everything in the bag that is
 * a real setting of the device's class. The 3D-model slot lives in the same bag
 * (card t_6b2c5f01) and is edited by its own section above, so it is not a row
 * here and does not, on its own, make the section appear.
 */
const settingFieldKeys = computed(() =>
  Object.keys(deviceSettings.value).filter((key) => key !== DEVICE_MODEL_KEY && key !== RETIRED_LIGHT_KEY),
);

/**
 * A slot edited in the section above is written straight into the device's
 * settings bag — the editor's own Save PATCHes the bag with the device, so a
 * model attached here lands on the DEVICE (nothing about it rides a widget), and
 * a removal is the key leaving the bag.
 */
function onDeviceModelEdited(model: DeviceModel3d | null): void {
  if (model) deviceSettings.value[DEVICE_MODEL_KEY] = deviceModelJson(model);
  else delete deviceSettings.value[DEVICE_MODEL_KEY];
}

/**
 * Slovene agrees with the count (1 vrednost, 2 vrednosti, 5 vrednosti); the last
 * two digits decide, as the language does (`21` = enaindvajset -> plural). The
 * same helper the scene widget, the room status strip and the drawers carry.
 */
function slCount(n: number, one: string, two: string, few: string, many: string): string {
  const lastTwo = n % 100;
  if (lastTwo === 1) return one;
  if (lastTwo === 2) return two;
  if (lastTwo === 3 || lastTwo === 4) return few;
  return many;
}

// Form data
const formData = ref<DeviceFormData>({
  deviceName: '',
  shortName: null,
  speechFullName: null,
  speechShortName: null,
  brandName: null,
  productName: null,
  modelNumber: null,
  manualUrl: null,
  productUrl: null,
  integrationId: props.integrationId || props.device?.integrationId || '',
  roomId: props.initialRoomId || null,
  deviceType: '',
  icon: 'devices',
  enabled: true,
  config: {},
  settings: {},
  logicScript: '',
  templateId: null,
});

/**
 * Every brand already used in this house. Typing a brand should not mean
 * inventing a second spelling of it ("Bosch" / "bosch" / "BOSCH"), so the input
 * offers the existing ones as suggestions while staying free text.
 */
const brandSuggestions = computed(() => {
  const brands = new Set<string>();
  for (const device of entitiesStore.devices) {
    const brand = device.brandName?.trim();
    if (brand) brands.add(brand);
  }
  return [...brands].sort((a, b) => a.localeCompare(b));
});

// Device-specific data
const deviceSettings = ref<Record<string, unknown>>({});

/**
 * The automation categories the editor offers, in the order the product reads
 * them. `custom` is the automation bucket, not a device's category.
 */
const DEVICE_AUTOMATION_CATEGORIES = AUTOMATION_CATEGORY_OPTIONS.filter((option) => option.value !== 'custom');

/** The owner's override, or `null` while the device follows the hub's derivation. */
const deviceCategoryOverride = computed<string[] | null>(() => {
  const stored = deviceSettings.value.automationCategories;
  if (!Array.isArray(stored)) return null;
  const known = stored.filter((value): value is string => typeof value === 'string');
  return known.length > 0 ? known : null;
});

/** What the hub derives for this device — what Automatic resolves to. */
const derivedDeviceCategories = computed<string[]>(() =>
  Array.isArray(props.device?.automationCategories) ? [...props.device.automationCategories] : [],
);

function isDeviceCategorySelected(category: string): boolean {
  const selected = deviceCategoryOverride.value ?? derivedDeviceCategories.value;
  return selected.includes(category);
}

/** The first tick starts from the derived set, so the owner edits rather than replaces. */
function toggleDeviceCategory(category: string): void {
  const current = new Set(deviceCategoryOverride.value ?? derivedDeviceCategories.value);
  if (current.has(category)) current.delete(category);
  else current.add(category);

  // An empty list means "not set" to the hub, so unticking the last box is the
  // same request as Automatic — say it once, in the shape the hub reads.
  if (current.size === 0) delete deviceSettings.value.automationCategories;
  else deviceSettings.value.automationCategories = [...current];
}

function resetDeviceCategories(): void {
  delete deviceSettings.value.automationCategories;
}

const settingDefinitions = ref<Record<string, DeviceClassSetting>>({});
const previousEnumValues = ref<Record<string, unknown>>({});
const deviceValuesList = ref<DeviceValueRow[]>([]);
const isLoadingClass = ref(false); // Guard flag to prevent infinite loop when loading class
const controlKnx = ref<Record<string, { groupAddress: string; dpt: number }>>({});
const statusKnx = ref<Record<string, { groupAddress: string; dpt: number }>>({});
const controlMatter = ref<Record<string, { propertyId: string; clusterId: number; attributeId: number }>>({});
const statusMatter = ref<Record<string, { propertyId: string; clusterId: number; attributeId: number }>>({});
const controlBacnet = ref<
  Record<
    string,
    {
      propertyId: string;
      bacnetDeviceId?: string;
      deviceInstance: number;
      address: string;
      objectType: string;
      objectInstance: number;
      propertyName?: string;
      objectName?: string;
    }
  >
>({});
const statusBacnet = ref<
  Record<
    string,
    {
      propertyId: string;
      bacnetDeviceId?: string;
      deviceInstance: number;
      address: string;
      objectType: string;
      objectInstance: number;
      propertyName?: string;
      objectName?: string;
    }
  >
>({});
const controlModbus = ref<
  Record<
    string,
    {
      propertyId: string;
      slaveAddress: number;
      tableType: string;
      registerAddress: number;
      dataFormat: string;
      byteOrder?: string | null;
      registerName?: string;
    }
  >
>({});
const statusModbus = ref<
  Record<
    string,
    {
      propertyId: string;
      slaveAddress: number;
      tableType: string;
      registerAddress: number;
      dataFormat: string;
      byteOrder?: string | null;
      registerName?: string;
    }
  >
>({});
const controlMqtt = ref<
  Record<
    string,
    {
      propertyId: string;
      topicPath: string;
      propertyName?: string;
      logicalDeviceKey?: string;
      stateTopic?: string | null;
      commandTopic?: string | null;
      availabilityTopic?: string | null;
      payloadType: string;
      qos: number;
      retain: boolean;
    }
  >
>({});
const statusMqtt = ref<
  Record<
    string,
    {
      propertyId: string;
      topicPath: string;
      propertyName?: string;
      logicalDeviceKey?: string;
      stateTopic?: string | null;
      commandTopic?: string | null;
      availabilityTopic?: string | null;
      payloadType: string;
      qos: number;
      retain: boolean;
    }
  >
>({});
const controlESPHome = ref<Record<string, { esphomeEntityId: string; entityKey?: string; field?: string }>>({});
const statusESPHome = ref<Record<string, { esphomeEntityId: string; entityKey?: string; field?: string }>>({});
const controlZWave = ref<
  Record<string, { zwaveValueId: string; zwaveEndpointId: string; propertyName: string; ccId: number; ccName: string }>
>({});
const statusZWave = ref<
  Record<string, { zwaveValueId: string; zwaveEndpointId: string; propertyName: string; ccId: number; ccName: string }>
>({});
const controlZigbee = ref<
  Record<
    string,
    {
      zigbeeAttributeId: string;
      zigbeeEndpointId: string;
      attributeName: string;
      clusterId: number;
      clusterName: string;
    }
  >
>({});
const statusZigbee = ref<
  Record<
    string,
    {
      zigbeeAttributeId: string;
      zigbeeEndpointId: string;
      attributeName: string;
      clusterId: number;
      clusterName: string;
    }
  >
>({});
const controlHA = ref<
  Record<
    string,
    {
      haEntityId: string;
      entityId: string;
      domain: string;
      friendlyName?: string;
      haField?: string;
      haStateTrue?: string;
      haStateFalse?: string;
      scaleFactor?: number;
      valueOffset?: number;
      haDomain?: string;
      haService?: string;
      haServiceOn?: string;
      haServiceOff?: string;
      haServiceParam?: string;
    }
  >
>({});
const statusHA = ref<
  Record<
    string,
    {
      haEntityId: string;
      entityId: string;
      domain: string;
      friendlyName?: string;
      haField?: string;
      haStateTrue?: string;
      haStateFalse?: string;
      scaleFactor?: number;
      valueOffset?: number;
      haDomain?: string;
      haService?: string;
      haServiceOn?: string;
      haServiceOff?: string;
      haServiceParam?: string;
    }
  >
>({});

// Reinit values state
const reinitingValues = ref(false);

async function reinitValues() {
  if (!props.device?.id) return;
  const ok = await confirmBox(t('dialogs.deviceReinitConfirm'));
  if (!ok) return;
  reinitingValues.value = true;
  try {
    const data = await apiFetch(`/devices/${props.device.id}/reinit-values`, {
      method: 'POST',
    });
    deviceValuesList.value = data.values || [];

    // Clear all references since they were dropped
    controlKnx.value = {};
    statusKnx.value = {};
    controlMatter.value = {};
    statusMatter.value = {};
    controlBacnet.value = {};
    statusBacnet.value = {};
    controlModbus.value = {};
    statusModbus.value = {};
    controlMqtt.value = {};
    statusMqtt.value = {};
    controlESPHome.value = {};
    statusESPHome.value = {};
    controlZWave.value = {};
    statusZWave.value = {};
    controlZigbee.value = {};
    statusZigbee.value = {};
    controlHA.value = {};
    statusHA.value = {};

    // Force-reload the device class to refresh settingDefinitions and selectedClass
    const classesStore = useDeviceClassesStore();
    classesStore.clearCache();
    if (props.device.deviceType) {
      await loadDeviceClass(props.device.deviceType);
    }

    // Re-initialize empty reference entries from the new template mappings
    // (identical to the logic in loadDeviceData so the form fields are visible)
    const extractDptNumber = (dpt: unknown): number => {
      if (typeof dpt === 'number') return dpt;
      if (!dpt) return 1;
      const match = String(dpt).match(/(\d+)(?:\.(\d+))?/);
      return match ? parseInt(match[1]) : 1;
    };

    if (selectedClass.value && integrationType.value === 'knx') {
      deviceValuesList.value.forEach((value) => {
        const controlMapping = templateMappings.value.find(
          (m) => m.valueKey === value.valueKey && m.referenceType === 'control',
        );
        const statusMapping = templateMappings.value.find(
          (m) => m.valueKey === value.valueKey && m.referenceType === 'status',
        );
        if (controlMapping) {
          const dptString = Array.isArray(controlMapping.knxDpt) ? controlMapping.knxDpt[0] : controlMapping.knxDpt;
          controlKnx.value[value.valueKey] = { groupAddress: '', dpt: extractDptNumber(dptString) };
        }
        if (statusMapping) {
          const dptString = Array.isArray(statusMapping.knxDpt) ? statusMapping.knxDpt[0] : statusMapping.knxDpt;
          statusKnx.value[value.valueKey] = { groupAddress: '', dpt: extractDptNumber(dptString) };
        }
      });
    } else if (selectedClass.value && integrationType.value === 'matter') {
      deviceValuesList.value.forEach((value) => {
        const controlMapping = templateMappings.value.find(
          (m) => m.valueKey === value.valueKey && m.referenceType === 'control',
        );
        const statusMapping = templateMappings.value.find(
          (m) => m.valueKey === value.valueKey && m.referenceType === 'status',
        );
        controlMatter.value[value.valueKey] = {
          propertyId: '',
          clusterId: controlMapping?.matterClusterId ?? 0,
          attributeId: controlMapping?.matterAttributeId ?? 0,
        };
        statusMatter.value[value.valueKey] = {
          propertyId: '',
          clusterId: statusMapping?.matterClusterId ?? 0,
          attributeId: statusMapping?.matterAttributeId ?? 0,
        };
      });
    } else if (selectedClass.value && integrationType.value === 'bacnet') {
      deviceValuesList.value.forEach((value) => {
        const controlMapping = templateMappings.value.find(
          (m) => m.valueKey === value.valueKey && m.referenceType === 'control',
        );
        const statusMapping = templateMappings.value.find(
          (m) => m.valueKey === value.valueKey && m.referenceType === 'status',
        );
        if (controlMapping) {
          controlBacnet.value[value.valueKey] = {
            propertyId: '',
            deviceInstance: 0,
            address: '',
            objectType: controlMapping.bacnetObjectType || '',
            objectInstance: 0,
            propertyName: String(controlMapping.bacnetPropertyId || 'PRESENT_VALUE'),
          };
        }
        if (statusMapping) {
          statusBacnet.value[value.valueKey] = {
            propertyId: '',
            deviceInstance: 0,
            address: '',
            objectType: statusMapping.bacnetObjectType || '',
            objectInstance: 0,
            propertyName: String(statusMapping.bacnetPropertyId || 'PRESENT_VALUE'),
          };
        }
      });
    } else if (selectedClass.value && integrationType.value === 'modbus') {
      deviceValuesList.value.forEach((value) => {
        const controlMapping = templateMappings.value.find(
          (m) => m.valueKey === value.valueKey && m.referenceType === 'control',
        );
        const statusMapping = templateMappings.value.find(
          (m) => m.valueKey === value.valueKey && m.referenceType === 'status',
        );
        if (controlMapping) {
          controlModbus.value[value.valueKey] = {
            propertyId: '',
            slaveAddress: 0,
            tableType: controlMapping.modbusTableType || '',
            registerAddress: 0,
            dataFormat: controlMapping.modbusDataFormat || '',
          };
        }
        if (statusMapping) {
          statusModbus.value[value.valueKey] = {
            propertyId: '',
            slaveAddress: 0,
            tableType: statusMapping.modbusTableType || '',
            registerAddress: 0,
            dataFormat: statusMapping.modbusDataFormat || '',
          };
        }
      });
    }
  } catch (err) {
    console.error('Failed to reinitialize device values', err);
    toast.error(t('dialogs.deviceReinitFailed'));
  } finally {
    reinitingValues.value = false;
  }
}

// Custom values state
const customValues = computed<CustomValue[]>(() =>
  deviceValuesList.value.filter((v): v is CustomValue => v.source === 'user' && typeof v.id === 'string'),
);
const showAddCustomValue = ref(false);
const addingCustomValue = ref(false);
const newCustomValue = ref({
  valueKey: '',
  name: '',
  valueType: 'number',
  unit: '',
  settable: true,
});

// Custom value reference linking state
const expandedCustomValueIds = ref<Set<string>>(new Set());
// per-value-id reference state for editing existing custom values
const cvStatusRefs = ref<Record<string, ProtocolRef | null>>({});
const cvControlRefs = ref<Record<string, ProtocolRef | null>>({});
const cvTransformerConfigs = ref<Record<string, StoredTransformerConfig>>({});
const savingCvRefId = ref<string | null>(null);
/**
 * The reference maps hold whichever protocol the device speaks — one row per
 * custom value — while each picker below takes its own protocol's config, so
 * every binding narrows to the protocol it renders. The narrowing hands the
 * stored object straight through (identity included: the pickers watch their
 * model value) and answers `undefined` for a row that is not shaped like that
 * protocol yet, which the picker renders as "nothing linked".
 */
function isKnxConfig(ref: ProtocolRef | null | undefined): ref is KNXConfig {
  return !!ref && typeof ref.groupAddress === 'string' && typeof ref.dpt === 'number';
}

function asKnxConfig(ref: ProtocolRef | null | undefined): KNXConfig | undefined {
  return isKnxConfig(ref) ? ref : undefined;
}

function isMatterConfig(ref: ProtocolRef | null | undefined): ref is MatterConfig {
  return (
    !!ref &&
    typeof ref.propertyId === 'string' &&
    typeof ref.clusterId === 'number' &&
    typeof ref.attributeId === 'number'
  );
}

function asMatterConfig(ref: ProtocolRef | null | undefined): MatterConfig | undefined {
  return isMatterConfig(ref) ? ref : undefined;
}

function isHaRef(ref: ProtocolRef | null | undefined): ref is HAEntityRef {
  return (
    !!ref && typeof ref.haEntityId === 'string' && typeof ref.entityId === 'string' && typeof ref.domain === 'string'
  );
}

function asHaRef(ref: ProtocolRef | null | undefined): HAEntityRef | undefined {
  return isHaRef(ref) ? ref : undefined;
}

function isEspHomeRef(ref: ProtocolRef | null | undefined): ref is ESPHomeEntityRef {
  return !!ref && typeof ref.esphomeEntityId === 'string';
}

function asEspHomeRef(ref: ProtocolRef | null | undefined): ESPHomeEntityRef | undefined {
  return isEspHomeRef(ref) ? ref : undefined;
}

/**
 * The Z-Wave and Zigbee editors read the ids the hub stores and let the picker
 * fill the rest of the row in, so they take a partially wired reference.
 */
function asZWaveRef(ref: ProtocolRef | null | undefined): Partial<ZWaveValueRef> | undefined {
  return ref && typeof ref.zwaveValueId === 'string' ? ref : undefined;
}

function asZigbeeRef(ref: ProtocolRef | null | undefined): Partial<ZigbeeAttributeRef> | undefined {
  return ref && typeof ref.zigbeeAttributeId === 'string' ? ref : undefined;
}

/**
 * The hub writes a KNX row's datapoint type as `dptType` and, in older payloads,
 * as `dpt`; the reads below accept either spelling the way they always have.
 */
function knxDptOf(row: KnxDptRow | undefined): number | undefined {
  return row?.dptType || row?.dpt;
}

/**
 * `visibleWhen` compares a setting against the scalar the class declares. The bag
 * holds whatever JSON the hub stored — scalars, plus the picked-value rows the
 * value picker writes — so both operands go through this first: a boolean and
 * `null` compare numerically as they did, and anything else (a row, a setting the
 * class no longer declares) has no operand, which answers `false` for every
 * relational operator exactly as the raw values did.
 */
function settingOperand(value: unknown): string | number | boolean | undefined {
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return value;
  if (value === null) return 0;
  return undefined;
}

/**
 * The one setting the value picker owns: the hub stores the picked row itself, so
 * anything that is not a row (or has no `valueId`) reads as "nothing picked yet".
 */
function pickedDeviceValue(value: unknown): PickedValueRow | null {
  if (typeof value !== 'object' || value === null || !('valueId' in value)) return null;
  if (typeof value.valueId !== 'string') return null;
  return {
    valueId: value.valueId,
    deviceName: 'deviceName' in value && typeof value.deviceName === 'string' ? value.deviceName : undefined,
    propertyName: 'propertyName' in value && typeof value.propertyName === 'string' ? value.propertyName : undefined,
  };
}

// for the "Add value" form — optional source linking
const showNewCvLinkSection = ref(false);
const newCvStatusRef = ref<ProtocolRef | null>(null);
const newCvControlRef = ref<ProtocolRef | null>(null);
const newCvTransformerConfig = ref<StoredTransformerConfig>({});

function toggleCvExpand(id: string) {
  const next = new Set(expandedCustomValueIds.value);
  if (next.has(id)) {
    next.delete(id);
  } else {
    next.add(id);
    initializeCvRefState(id);
  }
  expandedCustomValueIds.value = next;
}

function initializeCvRefState(valueId: string) {
  if (cvStatusRefs.value[valueId] !== undefined) return; // already initialized
  const cv = customValues.value.find((v) => v.id === valueId);
  if (!cv) return;

  // Status reference
  const sRef = cv.statusReference;
  if (sRef) {
    const sTf = transformerConfigOf(sRef.transformerConfig);
    if (sRef.propertyType === 'homeassistant') {
      cvStatusRefs.value[valueId] = {
        haEntityId: sRef.propertyId,
        entityId: sTf?.entityId || '',
        domain: sTf?.domain || '',
        haField: sTf?.haField || 'state',
        haStateTrue: sTf?.haStateTrue,
        haStateFalse: sTf?.haStateFalse,
        scaleFactor: sTf?.scaleFactor,
        valueOffset: sTf?.valueOffset,
      };
    } else if (sRef.propertyType === 'knx') {
      cvStatusRefs.value[valueId] = {
        groupAddress: sTf?.groupAddress || cv.statusKnx?.groupAddress || '',
        dpt: knxDptOf(cv.statusKnx) || 1,
      };
    } else if (sRef.propertyType === 'matter') {
      cvStatusRefs.value[valueId] = {
        propertyId: sRef.propertyId,
        clusterId: cv.statusMatter?.clusterId || 0,
        attributeId: cv.statusMatter?.attributeId || 0,
      };
    } else if (sRef.propertyType === 'esphome') {
      cvStatusRefs.value[valueId] = {
        esphomeEntityId: sRef.propertyId,
        entityKey: sRef.protocolKey || '',
        field: sTf?.field || '',
      };
    } else if (sRef.propertyType === 'zwave') {
      cvStatusRefs.value[valueId] = { zwaveValueId: sRef.propertyId, zwaveEndpointId: '' };
    } else if (sRef.propertyType === 'zigbee') {
      cvStatusRefs.value[valueId] = { zigbeeAttributeId: sRef.propertyId, zigbeeEndpointId: '' };
    } else {
      cvStatusRefs.value[valueId] = null;
    }
    if (sTf) {
      cvTransformerConfigs.value[valueId] = toEditorTransformerConfig(sRef.propertyType, sTf);
    }
  } else {
    cvStatusRefs.value[valueId] = null;
  }

  // Control reference
  const cRef = cv.controlReference;
  if (cRef) {
    const cTf = transformerConfigOf(cRef.transformerConfig);
    if (cRef.propertyType === 'homeassistant') {
      cvControlRefs.value[valueId] = {
        haEntityId: cRef.propertyId,
        entityId: cTf?.entityId || '',
        domain: cTf?.domain || '',
        haDomain: cTf?.haDomain || '',
        haService: cTf?.haService || '',
        haServiceOn: cTf?.haServiceOn || '',
        haServiceOff: cTf?.haServiceOff || '',
        haServiceParam: cTf?.haServiceParam || '',
      };
    } else if (cRef.propertyType === 'knx') {
      cvControlRefs.value[valueId] = {
        groupAddress: cv.controlKnx?.groupAddress || '',
        dpt: knxDptOf(cv.controlKnx) || 1,
      };
    } else if (cRef.propertyType === 'matter') {
      cvControlRefs.value[valueId] = {
        propertyId: cRef.propertyId,
        clusterId: cv.controlMatter?.clusterId || 0,
        attributeId: cv.controlMatter?.attributeId || 0,
      };
    } else if (cRef.propertyType === 'esphome') {
      cvControlRefs.value[valueId] = {
        esphomeEntityId: cRef.propertyId,
        entityKey: cRef.protocolKey || '',
        field: cTf?.field || '',
      };
    } else {
      cvControlRefs.value[valueId] = null;
    }
  } else {
    cvControlRefs.value[valueId] = null;
  }
}

/**
 * The protocol badge of a reference: the fields below are the only ones a badge
 * needs, and the callers hand it the editor's own picked `ProtocolRef` *or* the
 * hub's stored `DeviceValueReference` row (whose `transformerConfig` is JSON, not
 * the editor's parsed shape) — so the parameter asks for the one field it reads.
 */
function cvRefProtocolLabel(ref: { propertyType?: string } | null | undefined): string {
  if (!ref) return '';
  if (ref.propertyType === 'homeassistant') return 'HA';
  if (ref.propertyType === 'knx') return 'KNX';
  if (ref.propertyType === 'matter') return 'Matter';
  if (ref.propertyType === 'esphome') return 'ESPHome';
  if (ref.propertyType === 'zwave') return 'Z-Wave';
  if (ref.propertyType === 'zigbee') return 'Zigbee';
  return ref.propertyType ?? '';
}

function toEditorTransformerConfig(
  propertyType: string,
  transformerConfig: StoredTransformerConfig,
): StoredTransformerConfig {
  if (!transformerConfig) {
    return {};
  }

  if (propertyType !== 'esphome') {
    return { ...transformerConfig };
  }

  const normalized = { ...transformerConfig };
  if (normalized.trueValue !== undefined && normalized.haStateTrue === undefined) {
    normalized.haStateTrue = String(normalized.trueValue);
  }
  if (normalized.falseValue !== undefined && normalized.haStateFalse === undefined) {
    normalized.haStateFalse = String(normalized.falseValue);
  }
  delete normalized.trueValue;
  delete normalized.falseValue;
  delete normalized.field;
  return normalized;
}

function buildESPHomeTransformerConfig(
  transformerConfig: StoredTransformerConfig,
  field?: string,
): StoredTransformerConfig | undefined {
  const normalized = { ...(transformerConfig || {}) };

  if (normalized.haStateTrue !== undefined && normalized.trueValue === undefined) {
    normalized.trueValue = normalized.haStateTrue;
  }
  if (normalized.haStateFalse !== undefined && normalized.falseValue === undefined) {
    normalized.falseValue = normalized.haStateFalse;
  }

  delete normalized.haStateTrue;
  delete normalized.haStateFalse;

  if (field && field.trim()) {
    normalized.field = field.trim();
  } else {
    delete normalized.field;
  }

  return Object.keys(normalized).length > 0 ? normalized : undefined;
}

/** Build the HA transformer config object for a device value reference payload. */
function buildHATransformerConfig(ref: ProtocolRef | null | undefined): StoredTransformerConfig | undefined {
  if (!ref) return undefined;
  const normalized: StoredTransformerConfig = {};
  if (ref.haField !== undefined && ref.haField !== null && ref.haField !== '') {
    normalized.haField = ref.haField;
  }
  if (ref.haStateTrue !== undefined && ref.haStateTrue !== null && ref.haStateTrue !== '') {
    normalized.haStateTrue = ref.haStateTrue;
  }
  if (ref.haStateFalse !== undefined && ref.haStateFalse !== null && ref.haStateFalse !== '') {
    normalized.haStateFalse = ref.haStateFalse;
  }
  if (ref.scaleFactor !== undefined && ref.scaleFactor !== null) {
    normalized.scaleFactor = ref.scaleFactor;
  }
  if (ref.valueOffset !== undefined && ref.valueOffset !== null) {
    normalized.valueOffset = ref.valueOffset;
  }
  if (ref.haDomain !== undefined && ref.haDomain !== null && ref.haDomain !== '') {
    normalized.haDomain = ref.haDomain;
  }
  if (ref.haService !== undefined && ref.haService !== null && ref.haService !== '') {
    normalized.haService = ref.haService;
  }
  if (ref.haServiceOn !== undefined && ref.haServiceOn !== null && ref.haServiceOn !== '') {
    normalized.haServiceOn = ref.haServiceOn;
  }
  if (ref.haServiceOff !== undefined && ref.haServiceOff !== null && ref.haServiceOff !== '') {
    normalized.haServiceOff = ref.haServiceOff;
  }
  if (ref.haServiceParam !== undefined && ref.haServiceParam !== null && ref.haServiceParam !== '') {
    normalized.haServiceParam = ref.haServiceParam;
  }
  return Object.keys(normalized).length > 0 ? normalized : undefined;
}

async function saveCvReferences(cv: CustomValue) {
  if (!props.device?.id) return;
  savingCvRefId.value = cv.id;
  try {
    const statusRef = cvStatusRefs.value[cv.id];
    const controlRef = cvControlRefs.value[cv.id];
    const transformerConfig = cvTransformerConfigs.value[cv.id] || {};
    const payload: ReferenceUpdate = {};

    if (statusRef !== null && statusRef !== undefined) {
      payload.status = buildRefPayload(statusRef, 'status', transformerConfig);
    } else {
      payload.status = null; // clear
    }
    if (controlRef !== null && controlRef !== undefined) {
      payload.control = buildRefPayload(controlRef, 'control', {});
    } else {
      payload.control = null; // clear
    }

    await apiFetch(`/devices/${props.device.id}/values/${cv.id}/references`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
    // Reload device values to reflect updated refs
    const deviceData = await apiFetch(`/devices/${props.device.id}`);
    if (deviceData.values) deviceValuesList.value = deviceData.values;
    // Collapse row after save
    const next = new Set(expandedCustomValueIds.value);
    next.delete(cv.id);
    expandedCustomValueIds.value = next;
  } catch (err) {
    console.error('Failed to save cv references', err);
    toast.error(t('dialogs.deviceRefsSaveFailed'));
  } finally {
    savingCvRefId.value = null;
  }
}

function buildRefPayload(
  ref: ProtocolRef | null,
  _type: string,
  transformerConfig: StoredTransformerConfig,
): ReferencePayload | null {
  if (!ref) return null;
  // HA
  if (ref.haEntityId) {
    return {
      propertyType: 'homeassistant',
      haEntityId: ref.haEntityId,
      entityId: ref.entityId,
      transformerConfig: {
        entityId: ref.entityId,
        domain: ref.domain || ref.haDomain,
        haField: ref.haField,
        haStateTrue: ref.haStateTrue,
        haStateFalse: ref.haStateFalse,
        scaleFactor: ref.scaleFactor,
        valueOffset: ref.valueOffset,
        haDomain: ref.haDomain || ref.domain,
        haService: ref.haService,
        haServiceOn: ref.haServiceOn,
        haServiceOff: ref.haServiceOff,
        haServiceParam: ref.haServiceParam,
        ...transformerConfig,
      },
    };
  }
  // KNX
  if ('groupAddress' in ref) {
    return { propertyType: 'knx', groupAddress: ref.groupAddress, dpt: ref.dpt };
  }
  // ZWave
  if (ref.zwaveValueId) {
    return { propertyType: 'zwave', zwaveValueId: ref.zwaveValueId, zwaveEndpointId: ref.zwaveEndpointId };
  }
  // Zigbee
  if (ref.zigbeeAttributeId) {
    return { propertyType: 'zigbee', zigbeeAttributeId: ref.zigbeeAttributeId, zigbeeEndpointId: ref.zigbeeEndpointId };
  }
  // ESPHome
  if (ref.esphomeEntityId) {
    const esphomeTransformerConfig = buildESPHomeTransformerConfig(transformerConfig, ref.field);
    return {
      propertyType: 'esphome',
      esphomeEntityId: ref.esphomeEntityId,
      entityKey: ref.entityKey,
      transformerConfig: esphomeTransformerConfig,
    };
  }
  // Matter
  if (ref.propertyId !== undefined) {
    return {
      propertyType: 'matter',
      propertyId: ref.propertyId,
      clusterId: ref.clusterId,
      attributeId: ref.attributeId,
    };
  }
  return null;
}

const matterEndpointId = computed<string | undefined>(() =>
  props.device?.endpoint?.endpointType === 'matter' ? props.device.endpoint.protocolEndpointId : undefined,
);

/**
 * The device-type picker's group headings. The id — not the label — groups the
 * classes and keys the picker, so a label can be a pair (`[English, Slovensko]`)
 * without breaking `:key` (a tuple is not a valid vnode key).
 */
const DEVICE_CLASS_CATEGORIES: { id: string; label: [string, string] }[] = [
  { id: 'security_access', label: ['Security & Access', 'Varnost in dostop'] },
  { id: 'lighting', label: ['Lighting', 'Razsvetljava'] },
  { id: 'covers', label: ['Covers', 'Zasloni'] },
  { id: 'climate_hvac', label: ['Climate & HVAC', 'Klima in ogrevanje'] },
  { id: 'spa_wellness', label: ['Spa & Wellness', 'Spa in wellness'] },
  { id: 'energy', label: ['Energy', 'Energija'] },
  { id: 'metering', label: ['Metering', 'Merjenje'] },
  { id: 'water', label: ['Water', 'Voda'] },
  { id: 'switches_outlets', label: ['Switches & Outlets', 'Stikala in vtičnice'] },
  { id: 'sensors', label: ['Sensors', 'Senzorji'] },
  { id: 'automation', label: ['Automation', 'Avtomatizacija'] },
  { id: 'generic', label: ['Generic', 'Splošno'] },
  { id: 'other', label: ['Other', 'Drugo'] },
];

const DEVICE_CLASS_CATEGORY_ORDER = DEVICE_CLASS_CATEGORIES.map((category) => category.id);
const CATEGORY_LABEL_BY_ID: Record<string, [string, string]> = Object.fromEntries(
  DEVICE_CLASS_CATEGORIES.map((category) => [category.id, category.label] as [string, [string, string]]),
);

/** A group heading in the reader's language. */
function categoryLabel(category: string): string {
  const label = CATEGORY_LABEL_BY_ID[category];
  return label ? tt(label[0], label[1]) : category;
}

function getDeviceClassCategoryId(deviceClass: DeviceClassMetadata): string {
  const rawCategory = String(deviceClass.category || '').toLowerCase();
  const controlType = String(deviceClass.controlType || '').toLowerCase();
  const classId = String(deviceClass.classId || '').toLowerCase();

  if (['water_tank', 'water_meter', 'water_heater'].includes(controlType) || classId.startsWith('water_'))
    return 'water';
  if (controlType === 'light' || rawCategory === 'lighting') return 'lighting';
  if (controlType === 'cover' || rawCategory === 'covers') return 'covers';
  if (['thermostat', 'climate', 'fan'].includes(controlType) || ['climate', 'hvac'].includes(rawCategory))
    return 'climate_hvac';
  if (['sauna', 'pool', 'hot_tub', 'fitness_equipment'].includes(controlType) || rawCategory === 'spa_wellness')
    return 'spa_wellness';
  if (rawCategory === 'metering') return 'metering';
  if (rawCategory === 'energy' || ['power_meter', 'power_meter_3phase', 'water_meter'].includes(controlType))
    return 'energy';
  if (['sensor', 'binary_sensor'].includes(controlType) || rawCategory === 'sensor' || rawCategory === 'sensors')
    return 'sensors';
  if (['lock', 'strike_lock', 'motorized_lock', 'alarm', 'camera'].includes(controlType) || rawCategory === 'security')
    return 'security_access';
  if (controlType === 'scene' || rawCategory === 'automation') return 'automation';
  if (controlType === 'switch' || rawCategory === 'switches' || rawCategory === 'appliances') return 'switches_outlets';
  if (rawCategory === 'generic') return 'generic';
  return 'other';
}

const classesByCategory = computed(() => {
  const grouped: Record<string, DeviceClassMetadata[]> = {};

  for (const deviceClass of availableClasses.value) {
    const category = getDeviceClassCategoryId(deviceClass);
    if (!grouped[category]) {
      grouped[category] = [];
    }
    grouped[category].push(deviceClass);
  }

  for (const classes of Object.values(grouped)) {
    classes.sort((left, right) =>
      String(left.name || '').localeCompare(String(right.name || ''), undefined, {
        sensitivity: 'base',
      }),
    );
  }

  return DEVICE_CLASS_CATEGORY_ORDER.reduce(
    (result, category) => {
      if (grouped[category]?.length) {
        result[category] = grouped[category];
      }
      return result;
    },
    {} as Record<string, DeviceClassMetadata[]>,
  );
});

/** Rooms in no area share one group; the hub's own area names pass through. */
const UNASSIGNED_AREA = 'Unassigned';

/** The room picker's group heading: this file's own sentinel is copy, hub areas are data. */
function areaGroupLabel(area: string): string {
  return area === UNASSIGNED_AREA ? tt('Unassigned', 'Nedodeljeno') : area;
}

const roomsByArea = computed(() => {
  const grouped: Record<string, Room[]> = {};

  // Group rooms by actual area name from the store
  for (const room of entitiesStore.rooms) {
    let areaName = UNASSIGNED_AREA;

    // If room has an areaId, look up the area name from the store
    if (room.areaId) {
      const area = entitiesStore.getArea(room.areaId);
      if (area) {
        areaName = area.areaName;
      }
    }

    if (!grouped[areaName]) {
      grouped[areaName] = [];
    }
    grouped[areaName].push(room);
  }

  // Sort areas alphabetically, with 'Unassigned' at the end
  return Object.keys(grouped)
    .sort((a, b) => {
      if (a === UNASSIGNED_AREA) return 1;
      if (b === UNASSIGNED_AREA) return -1;
      return a.localeCompare(b);
    })
    .reduce(
      (result, area) => {
        result[area] = grouped[area];
        return result;
      },
      {} as Record<string, Room[]>,
    );
});

const templateMappings = computed<PropertyMapping[]>(() => {
  console.log('[DeviceEditor] templateMappings computed - selectedClass:', selectedClass.value?.classId);
  if (!selectedClass.value) {
    console.log('[DeviceEditor] templateMappings: no selectedClass, returning []');
    return [];
  }

  // Device class structure has direct 'mappings' property
  if (selectedClass.value.mappings) {
    console.log('[DeviceEditor] templateMappings: using selectedClass.mappings', selectedClass.value.mappings);

    // Canonical unified classes declare mappings for every protocol they
    // support. Only render the ones that apply to this device's integration,
    // otherwise the editor shows one reference input per protocol per value.
    const proto = (integrationType.value || '').toLowerCase();
    const applicable = selectedClass.value.mappings.filter(
      (m) => !m.protocolType || m.protocolType.toLowerCase() === proto,
    );

    if (applicable.length > 0) {
      return applicable;
    }

    // No mappings for this protocol: canonical classes are still usable via
    // manual value linking, so show the fallback reference UI instead of
    // another protocol's mappings.
    const hasScopedMappings = selectedClass.value.mappings.some((m) => !!m.protocolType);
    if (hasScopedMappings) {
      return [];
    }

    // Legacy classes without protocol-scoped mappings: keep previous behaviour.
    return selectedClass.value.mappings;
  }

  // Fallback for integrationMappings
  if (selectedClass.value.integrationMappings) {
    const mappings =
      integrationType.value === 'knx'
        ? selectedClass.value.integrationMappings.knx
        : selectedClass.value.integrationMappings.matter;
    console.log('[DeviceEditor] templateMappings: using integrationMappings', mappings);
    return mappings ?? [];
  }

  console.log('[DeviceEditor] templateMappings: no mappings found, returning []');
  return [];
});

// Thumbnail upload/remove
const imageAssetsStore = useImageAssetsStore();

async function pickThumbnail() {
  if (thumbnailUploading.value || !props.device?.id) return;
  try {
    const file = await pickImageFile({ title: tt('Select Device Thumbnail', 'Izberi sliko naprave') });
    if (file) await uploadDeviceThumbnail(file);
  } catch (err) {
    console.error('Device thumbnail pick error', err);
    toast.error(err instanceof Error ? err.message : String(err));
  }
}

async function uploadDeviceThumbnail(blob: Blob) {
  if (!props.device?.id) return;

  thumbnailUploading.value = true;
  try {
    const formPayload = new FormData();
    formPayload.append('image', blob, 'thumbnail.png');

    const response = await apiFetchRaw(`/devices/${props.device.id}/thumbnail`, {
      method: 'POST',
      body: formPayload,
    });
    const data = await response.json();

    thumbnailRemoved.value = false;
    // Show the bytes the hub actually stored, fetched through the app's own
    // transport. The presigned URL in `data.thumbnailUrl` is unreachable from
    // Electron's cloud/WebRTC surface, so using it left the preview blank until
    // the dialog was reopened.
    const previewUrl = data.s3Key ? await imageAssetsStore.fetchImageUrlByS3Key(data.s3Key, true) : null;
    thumbnailPreview.value = previewUrl ?? data.thumbnailUrl ?? null;
  } catch (err) {
    console.error('Thumbnail upload error', err);
    toast.error(t('dialogs.thumbnailUploadFailed', { message: err instanceof Error ? err.message : err }));
  } finally {
    thumbnailUploading.value = false;
  }
}

async function removeThumbnail() {
  if (!props.device?.id) return;
  try {
    await apiFetch(`/devices/${props.device.id}/thumbnail`, {
      method: 'DELETE',
    });
    thumbnailPreview.value = null;
    thumbnailRemoved.value = true;
  } catch (err) {
    console.error('Thumbnail remove error', err);
    toast.error(t('dialogs.thumbnailRemoveFailed', { message: err instanceof Error ? err.message : err }));
  }
}

// Drag & drop on the thumbnail row
function onThumbnailDragEnter(event: DragEvent) {
  if (event.dataTransfer && Array.from(event.dataTransfer.types).includes('Files')) {
    thumbnailDragging.value = true;
  }
}

function onThumbnailDragLeave(event: DragEvent) {
  const area = event.currentTarget as HTMLElement | null;
  const next = event.relatedTarget as Node | null;
  if (!area || !next || !area.contains(next)) {
    thumbnailDragging.value = false;
  }
}

async function onThumbnailDrop(event: DragEvent) {
  thumbnailDragging.value = false;
  if (thumbnailUploading.value) return;
  const file = firstImageFromDrop(event);
  if (file) await uploadDeviceThumbnail(file);
}

// ── State image (binary sensors: the picture shown while active) ──────────
async function pickStateImage() {
  if (stateThumbUploading.value || !props.device?.id) return;
  try {
    const file = await pickImageFile({ title: tt('Select State Image', 'Izberi sliko stanja') });
    if (file) await uploadStateImage(file);
  } catch (err) {
    console.error('State image pick error', err);
    toast.error(err instanceof Error ? err.message : String(err));
  }
}

async function uploadStateImage(blob: Blob) {
  if (!props.device?.id) return;
  stateThumbUploading.value = true;
  try {
    const formPayload = new FormData();
    formPayload.append('image', blob, 'state.png');
    const response = await apiFetchRaw(`/devices/${props.device.id}/state-thumbnail`, {
      method: 'POST',
      body: formPayload,
    });
    const data = await response.json();

    stateThumbRemoved.value = false;
    // Show the bytes the hub actually stored, fetched through the app's own
    // transport — the presigned URL is unreachable in cloud/WebRTC mode.
    const previewUrl = data.s3Key ? await imageAssetsStore.fetchImageUrlByS3Key(data.s3Key, true) : null;
    stateThumbPreview.value = previewUrl ?? data.stateThumbnailUrl ?? null;
  } catch (err) {
    console.error('State image upload error', err);
    toast.error(t('dialogs.stateThumbnailUploadFailed', { message: err instanceof Error ? err.message : err }));
  } finally {
    stateThumbUploading.value = false;
  }
}

async function removeStateImage() {
  if (!props.device?.id) return;
  stateThumbUploading.value = true;
  try {
    await apiFetch(`/devices/${props.device.id}/state-thumbnail`, { method: 'DELETE' });
    stateThumbPreview.value = null;
    stateThumbRemoved.value = true;
  } catch (err) {
    console.error('State image remove error', err);
    toast.error(t('dialogs.stateThumbnailRemoveFailed', { message: err instanceof Error ? err.message : err }));
  } finally {
    stateThumbUploading.value = false;
  }
}

// Custom values methods
async function addCustomValue() {
  if (!props.device?.id || !newCustomValue.value.valueKey.trim() || !newCustomValue.value.name.trim()) return;
  addingCustomValue.value = true;
  try {
    const created = await apiFetch(`/devices/${props.device.id}/values`, {
      method: 'POST',
      body: JSON.stringify({
        valueKey: newCustomValue.value.valueKey.trim(),
        name: newCustomValue.value.name.trim(),
        valueType: newCustomValue.value.valueType,
        unit: newCustomValue.value.unit.trim() || null,
        settable: newCustomValue.value.settable,
      }),
    });
    deviceValuesList.value.push(created);

    // If user linked a source, immediately patch references
    if (showNewCvLinkSection.value && (newCvStatusRef.value || newCvControlRef.value)) {
      const payload: ReferenceUpdate = {};
      if (newCvStatusRef.value) {
        payload.status = buildRefPayload(newCvStatusRef.value, 'status', newCvTransformerConfig.value || {});
      }
      if (newCvControlRef.value) {
        payload.control = buildRefPayload(newCvControlRef.value, 'control', {});
      }
      await apiFetch(`/devices/${props.device.id}/values/${created.id}/references`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      }).catch((e) => console.error('Failed to create inline references:', e));
      // Reload to get the linked data
      const devData = await apiFetch(`/devices/${props.device.id}`);
      if (devData.values) deviceValuesList.value = devData.values;
    }

    showAddCustomValue.value = false;
    showNewCvLinkSection.value = false;
    newCustomValue.value = { valueKey: '', name: '', valueType: 'number', unit: '', settable: true };
    newCvStatusRef.value = null;
    newCvControlRef.value = null;
    newCvTransformerConfig.value = {};
  } catch (err) {
    console.error('Failed to add custom value', err);
  } finally {
    addingCustomValue.value = false;
  }
}

async function deleteCustomValue(cv: CustomValue) {
  if (!props.device?.id) return;
  const ok = await confirmBox(t('dialogs.customValueDeleteConfirm', { name: cv.name }));
  if (!ok) return;
  try {
    await apiFetch(`/devices/${props.device.id}/values/${cv.id}`, {
      method: 'DELETE',
    });
    deviceValuesList.value = deviceValuesList.value.filter((v) => v.id !== cv.id);
  } catch (err) {
    console.error('Failed to delete custom value', err);
  }
}

// Lifecycle
onMounted(async () => {
  await initialize();
});

// The settings modal / editor can be REUSED across devices (the :device prop
// changes without the component being remounted). Re-initialize the whole form
// when the device id changes so no section (values, settings, automations…)
// keeps showing the previously edited device's data.
watch(
  () => props.device?.id,
  async (newId, oldId) => {
    if (!newId || newId === oldId) return;
    if (props.mode === 'edit' && oldId) {
      socketStore.unsubscribe('device', oldId);
      socketStore.off('value_changed', handleValueChanged);
    }
    await initialize();
    stateThumbPreview.value = null;
    stateThumbRemoved.value = false;
  },
);

// Watch for template selection changes
watch(selectedClassId, async (newClassId, oldClassId) => {
  console.log('[DeviceEditor] selectedClassId changed from', oldClassId, 'to', newClassId);
  console.log('[DeviceEditor] Type of newClassId:', typeof newClassId);
  // Handle both null and undefined, but require a truthy value (actual class ID string)
  if (newClassId && newClassId !== 'null' && newClassId !== 'undefined' && props.mode === 'add') {
    console.log('[DeviceEditor] Loading device class due to watch trigger');
    await loadDeviceClass(newClassId);

    // Auto-fill device name if empty
    if (!formData.value.deviceName || formData.value.deviceName.trim() === '') {
      if (selectedClass.value?.name) {
        formData.value.deviceName = selectedClass.value.name;
      }
    }
  } else {
    console.log('[DeviceEditor] Skipping class load - invalid classId or not in add mode');
  }
});

// Debounce timer for settings watcher to avoid race conditions with rapid input changes
let settingsDebounceTimer: ReturnType<typeof setTimeout> | null = null;

// Watch for settings changes to reload device class with updated values
watch(
  deviceSettings,
  (newSettings) => {
    // Don't trigger reload while we're loading a class (prevents infinite loop)
    if (isLoadingClass.value || !selectedClassId.value) return;
    if (props.mode !== 'add' && props.mode !== 'edit') return;

    // Check if any settings have actually changed value (enum, boolean, number, etc.)
    let settingChanged = false;

    Object.entries(settingDefinitions.value).forEach(([key]: [string, unknown]) => {
      // A drawing pick changes the picture, never the values: no reload.
      if (isDrawingSetting(String(key))) return;
      const newValue = newSettings[key];
      const oldValue = previousEnumValues.value[key];

      if (newValue !== oldValue) {
        console.log(`[DeviceEditor] Setting "${key}" changed from "${oldValue}" to "${newValue}"`);
        settingChanged = true;
        previousEnumValues.value[key] = newValue;
      }
    });

    if (settingChanged) {
      // Debounce to avoid stale responses overwriting correct ones when typing in number inputs
      if (settingsDebounceTimer) clearTimeout(settingsDebounceTimer);
      settingsDebounceTimer = setTimeout(async () => {
        console.log('[DeviceEditor] Setting changed, reloading device values with new settings');
        // Update formData settings and reload class to get updated mappings and values
        formData.value.settings = JSON.parse(JSON.stringify(deviceSettings.value));

        // Clear class cache to ensure we get fresh mappings for the new settings
        const classesStore = useDeviceClassesStore();
        classesStore.clearCache();

        if (props.mode === 'edit') {
          await reloadDeviceValuesWithSettingsEditMode(selectedClassId.value!);
        } else {
          await reloadDeviceValuesWithSettings(selectedClassId.value!);
        }
      }, 350);
    }
  },
  { deep: true },
);

onUnmounted(() => {
  if (settingsDebounceTimer) clearTimeout(settingsDebounceTimer);
  if (props.mode === 'edit' && props.device) {
    socketStore.unsubscribe('device', props.device.id);
    socketStore.off('value_changed', handleValueChanged);
  }
});

// Methods
async function initialize() {
  loading.value = true;

  try {
    // Initialize device classes store
    await deviceClassesStore.initialize();

    // Load integration type
    const effectiveIntegrationId = props.integrationId || props.device?.integrationId;
    if (!effectiveIntegrationId) {
      console.warn('No integration ID provided');
      loading.value = false;
      return;
    }

    const integration = await apiFetch(`/integrations/${effectiveIntegrationId}`);
    integrationType.value = integration.integrationType.toLowerCase();

    // Load available device classes for this integration type
    availableClasses.value = deviceClassesStore.getByIntegration(integrationType.value);
    console.log(
      '[DeviceEditor] Loaded device classes for',
      integrationType.value,
      ':',
      availableClasses.value.length,
      'classes',
    );
    console.log('[DeviceEditor] First device class sample:', availableClasses.value[0]);

    if (props.mode === 'edit' && props.device) {
      // Edit mode - load device data
      await loadDeviceData();
    }
  } catch (error) {
    console.error('Failed to initialize DeviceEditor:', error);
  } finally {
    loading.value = false;
  }
}

async function loadDeviceData() {
  if (!props.device) return;

  // Populate form data
  formData.value = {
    deviceName: props.device.deviceName,
    shortName: props.device.shortName ?? null,
    speechFullName: props.device.speechFullName ?? null,
    speechShortName: props.device.speechShortName ?? null,
    brandName: props.device.brandName ?? null,
    productName: props.device.productName ?? null,
    modelNumber: props.device.modelNumber ?? null,
    manualUrl: props.device.manualUrl ?? null,
    productUrl: props.device.productUrl ?? null,
    integrationId: props.device.integrationId ?? null,
    roomId: props.device.roomId ?? null,
    deviceType: props.device.deviceType ?? null,
    icon: props.device.icon ?? '',
    enabled: props.device.enabled,
    config: wireJsonBag(props.device.config),
    settings: props.device.settings ? JSON.parse(JSON.stringify(props.device.settings)) : {},
    logicScript: props.device.logicScript || '',
    templateId: props.device.templateId,
  };

  // Create a deep copy of settings to avoid reference issues
  deviceSettings.value = JSON.parse(JSON.stringify(formData.value.settings));
  selectedClassId.value = props.device.deviceType || null;

  console.log('[DeviceEditor] Loaded device settings:', deviceSettings.value);
  console.log('[DeviceEditor] Settings keys:', Object.keys(deviceSettings.value));

  // Load device values with references FIRST
  const fullDevice = await apiFetch(`/devices/${props.device.id}`);
  deviceValuesList.value = fullDevice.values || [];
  console.log('[DeviceEditor] Loaded device from API:', fullDevice);
  console.log('[DeviceEditor] Device values count:', fullDevice.values?.length);
  console.log('[DeviceEditor] Device values:', fullDevice.values);
  controlKnx.value = {};
  statusKnx.value = {};
  controlMatter.value = {};
  statusMatter.value = {};
  controlBacnet.value = {};
  statusBacnet.value = {};
  controlModbus.value = {};
  statusModbus.value = {};
  controlMqtt.value = {};
  statusMqtt.value = {};
  controlESPHome.value = {};
  statusESPHome.value = {};
  controlZWave.value = {};
  statusZWave.value = {};
  controlZigbee.value = {};
  statusZigbee.value = {};
  controlHA.value = {};
  statusHA.value = {};

  // Helper function to extract DPT number from string format
  const extractDptNumber = (dpt: unknown): number => {
    if (typeof dpt === 'number') return dpt;
    if (!dpt) return 1;
    // Handle formats like "DPT9.001", "DPT9", "9.001", "9"
    const match = String(dpt).match(/(\d+)(?:\.(\d+))?/);
    return match ? parseInt(match[1]) : 1;
  };

  // Populate references from actual device data
  deviceValuesList.value.forEach((value) => {
    console.log(`[DeviceEditor] Processing value ${value.valueKey}:`, {
      hasControlKnx: !!value.controlKnx,
      hasStatusKnx: !!value.statusKnx,
      hasControlMatter: !!value.controlMatter,
      hasStatusMatter: !!value.statusMatter,
      hasControlBacnet: !!value.controlBacnet,
      hasStatusBacnet: !!value.statusBacnet,
      hasControlModbus: !!value.controlModbus,
      hasStatusModbus: !!value.statusModbus,
      hasControlMqtt: !!value.controlMqtt,
      hasStatusMqtt: !!value.statusMqtt,
      hasControlESPHome: value.controlReference?.propertyType === 'esphome',
      hasStatusESPHome: value.statusReference?.propertyType === 'esphome',
      controlMatter: value.controlMatter,
      statusMatter: value.statusMatter,
      controlBacnet: value.controlBacnet,
      statusBacnet: value.statusBacnet,
      controlModbus: value.controlModbus,
      statusModbus: value.statusModbus,
      controlMqtt: value.controlMqtt,
      statusMqtt: value.statusMqtt,
    });

    if (value.controlKnx) {
      controlKnx.value[value.valueKey] = {
        groupAddress: value.controlKnx.groupAddress || '',
        dpt: extractDptNumber(knxDptOf(value.controlKnx)),
      };
    }

    if (value.statusKnx) {
      statusKnx.value[value.valueKey] = {
        groupAddress: value.statusKnx.groupAddress || '',
        dpt: extractDptNumber(knxDptOf(value.statusKnx)),
      };
    }

    if (value.controlMatter) {
      console.log(`[DeviceEditor] Setting controlMatter for ${value.valueKey}:`, value.controlMatter);
      controlMatter.value[value.valueKey] = {
        propertyId: value.controlMatter.propertyId || '',
        clusterId: value.controlMatter.clusterId || 0,
        attributeId: value.controlMatter.attributeId || 0,
      };
    }

    if (value.statusMatter) {
      console.log(`[DeviceEditor] Setting statusMatter for ${value.valueKey}:`, value.statusMatter);
      statusMatter.value[value.valueKey] = {
        propertyId: value.statusMatter.propertyId || '',
        clusterId: value.statusMatter.clusterId || 0,
        attributeId: value.statusMatter.attributeId || 0,
      };
    }

    if (value.controlBacnet) {
      controlBacnet.value[value.valueKey] = {
        propertyId: value.controlBacnet.propertyId || '',
        bacnetDeviceId: value.controlBacnet.bacnetDeviceId || undefined,
        deviceInstance: value.controlBacnet.deviceInstance || 0,
        address: value.controlBacnet.address || '',
        objectType: value.controlBacnet.objectType || '',
        objectInstance: value.controlBacnet.objectInstance || 0,
        propertyName: value.controlBacnet.propertyName || undefined,
        objectName: value.controlBacnet.objectName || undefined,
      };
    }

    if (value.statusBacnet) {
      statusBacnet.value[value.valueKey] = {
        propertyId: value.statusBacnet.propertyId || '',
        bacnetDeviceId: value.statusBacnet.bacnetDeviceId || undefined,
        deviceInstance: value.statusBacnet.deviceInstance || 0,
        address: value.statusBacnet.address || '',
        objectType: value.statusBacnet.objectType || '',
        objectInstance: value.statusBacnet.objectInstance || 0,
        propertyName: value.statusBacnet.propertyName || undefined,
        objectName: value.statusBacnet.objectName || undefined,
      };
    }

    if (value.controlModbus) {
      controlModbus.value[value.valueKey] = {
        propertyId: value.controlModbus.propertyId || '',
        slaveAddress: value.controlModbus.slaveAddress || 0,
        tableType: value.controlModbus.tableType || '',
        registerAddress: value.controlModbus.registerAddress || 0,
        dataFormat: value.controlModbus.dataFormat || '',
        byteOrder: value.controlModbus.byteOrder,
        registerName: value.controlModbus.registerName || undefined,
      };
    }

    if (value.statusModbus) {
      statusModbus.value[value.valueKey] = {
        propertyId: value.statusModbus.propertyId || '',
        slaveAddress: value.statusModbus.slaveAddress || 0,
        tableType: value.statusModbus.tableType || '',
        registerAddress: value.statusModbus.registerAddress || 0,
        dataFormat: value.statusModbus.dataFormat || '',
        byteOrder: value.statusModbus.byteOrder,
        registerName: value.statusModbus.registerName || undefined,
      };
    }

    if (value.controlMqtt) {
      controlMqtt.value[value.valueKey] = {
        propertyId: value.controlMqtt.propertyId || '',
        topicPath: value.controlMqtt.topicPath || '',
        propertyName: value.controlMqtt.propertyName || undefined,
        logicalDeviceKey: value.controlMqtt.logicalDeviceKey || undefined,
        stateTopic: value.controlMqtt.stateTopic || undefined,
        commandTopic: value.controlMqtt.commandTopic || undefined,
        availabilityTopic: value.controlMqtt.availabilityTopic || undefined,
        payloadType: value.controlMqtt.payloadType || 'string',
        qos: value.controlMqtt.qos ?? 0,
        retain: !!value.controlMqtt.retain,
      };
    }

    if (value.statusMqtt) {
      statusMqtt.value[value.valueKey] = {
        propertyId: value.statusMqtt.propertyId || '',
        topicPath: value.statusMqtt.topicPath || '',
        propertyName: value.statusMqtt.propertyName || undefined,
        logicalDeviceKey: value.statusMqtt.logicalDeviceKey || undefined,
        stateTopic: value.statusMqtt.stateTopic || undefined,
        commandTopic: value.statusMqtt.commandTopic || undefined,
        availabilityTopic: value.statusMqtt.availabilityTopic || undefined,
        payloadType: value.statusMqtt.payloadType || 'string',
        qos: value.statusMqtt.qos ?? 0,
        retain: !!value.statusMqtt.retain,
      };
    }

    if (value.controlReference?.propertyType === 'esphome' && value.controlReference.propertyId) {
      controlESPHome.value[value.valueKey] = {
        esphomeEntityId: value.controlReference.propertyId,
        entityKey: value.controlReference.protocolKey || undefined,
        field: transformerConfigOf(value.controlReference.transformerConfig)?.field || undefined,
      };
    }

    if (value.statusReference?.propertyType === 'esphome' && value.statusReference.propertyId) {
      statusESPHome.value[value.valueKey] = {
        esphomeEntityId: value.statusReference.propertyId,
        entityKey: value.statusReference.protocolKey || undefined,
        field: transformerConfigOf(value.statusReference.transformerConfig)?.field || undefined,
      };
    }

    if (value.controlReference?.propertyType === 'zwave' && value.controlReference.propertyId) {
      controlZWave.value[value.valueKey] = {
        zwaveValueId: value.controlReference.propertyId,
        zwaveEndpointId: value.controlReference.protocolKey || '',
        propertyName: '',
        ccId: 0,
        ccName: '',
      };
    }

    if (value.statusReference?.propertyType === 'zwave' && value.statusReference.propertyId) {
      statusZWave.value[value.valueKey] = {
        zwaveValueId: value.statusReference.propertyId,
        zwaveEndpointId: value.statusReference.protocolKey || '',
        propertyName: '',
        ccId: 0,
        ccName: '',
      };
    }

    if (value.controlReference?.propertyType === 'zigbee' && value.controlReference.propertyId) {
      controlZigbee.value[value.valueKey] = {
        zigbeeAttributeId: value.controlReference.propertyId,
        zigbeeEndpointId: value.controlReference.protocolKey || '',
        attributeName: '',
        clusterId: 0,
        clusterName: '',
      };
    }

    if (value.statusReference?.propertyType === 'zigbee' && value.statusReference.propertyId) {
      statusZigbee.value[value.valueKey] = {
        zigbeeAttributeId: value.statusReference.propertyId,
        zigbeeEndpointId: value.statusReference.protocolKey || '',
        attributeName: '',
        clusterId: 0,
        clusterName: '',
      };
    }

    if (value.controlReference?.propertyType === 'homeassistant' && value.controlReference.propertyId) {
      const cfg = transformerConfigOf(value.controlReference.transformerConfig) ?? {};
      controlHA.value[value.valueKey] = {
        haEntityId: value.controlReference.propertyId,
        entityId: '',
        domain: '',
        haField: cfg.haField,
        haStateTrue: cfg.haStateTrue,
        haStateFalse: cfg.haStateFalse,
        scaleFactor: cfg.scaleFactor,
        valueOffset: cfg.valueOffset,
        haDomain: cfg.haDomain,
        haService: cfg.haService,
        haServiceOn: cfg.haServiceOn,
        haServiceOff: cfg.haServiceOff,
        haServiceParam: cfg.haServiceParam,
      };
    }

    if (value.statusReference?.propertyType === 'homeassistant' && value.statusReference.propertyId) {
      const cfg = transformerConfigOf(value.statusReference.transformerConfig) ?? {};
      statusHA.value[value.valueKey] = {
        haEntityId: value.statusReference.propertyId,
        entityId: '',
        domain: '',
        haField: cfg.haField,
        haStateTrue: cfg.haStateTrue,
        haStateFalse: cfg.haStateFalse,
        scaleFactor: cfg.scaleFactor,
        valueOffset: cfg.valueOffset,
        haDomain: cfg.haDomain,
        haService: cfg.haService,
        haServiceOn: cfg.haServiceOn,
        haServiceOff: cfg.haServiceOff,
        haServiceParam: cfg.haServiceParam,
      };
    }
  });

  console.log('[DeviceEditor] Final controlMatter:', controlMatter.value);
  console.log('[DeviceEditor] Final statusMatter:', statusMatter.value);
  console.log('[DeviceEditor] Final controlBacnet:', controlBacnet.value);
  console.log('[DeviceEditor] Final statusBacnet:', statusBacnet.value);
  console.log('[DeviceEditor] Final controlModbus:', controlModbus.value);
  console.log('[DeviceEditor] Final statusModbus:', statusModbus.value);
  console.log('[DeviceEditor] Final controlMqtt:', controlMqtt.value);
  console.log('[DeviceEditor] Final statusMqtt:', statusMqtt.value);
  console.log('[DeviceEditor] Final controlESPHome:', controlESPHome.value);
  console.log('[DeviceEditor] Final statusESPHome:', statusESPHome.value);

  // Load template to get DPT types for values without references
  if (props.device.deviceType) {
    await loadDeviceClass(props.device.deviceType);

    // After loading template, populate missing value references with correct DPT from template
    if (selectedClass.value && integrationType.value === 'knx') {
      const extractDptNumber = (dpt: unknown): number => {
        if (typeof dpt === 'number') return dpt;
        if (!dpt) return 1;
        // Handle formats like "DPT9.001", "DPT9", "9.001", "9"
        const match = String(dpt).match(/(\d+)(?:\.(\d+))?/);
        return match ? parseInt(match[1]) : 1;
      };

      // For each value in the device, ensure it has a reference entry with correct DPT
      deviceValuesList.value.forEach((value) => {
        // Find the correct DPT from template mappings
        const controlMapping = templateMappings.value.find(
          (m) => m.valueKey === value.valueKey && m.referenceType === 'control',
        );
        const statusMapping = templateMappings.value.find(
          (m) => m.valueKey === value.valueKey && m.referenceType === 'status',
        );

        // Only create control reference if mapping exists and reference doesn't exist yet
        if (controlMapping && !controlKnx.value[value.valueKey]) {
          const dptString = Array.isArray(controlMapping.knxDpt) ? controlMapping.knxDpt[0] : controlMapping.knxDpt;
          controlKnx.value[value.valueKey] = {
            groupAddress: '',
            dpt: extractDptNumber(dptString),
          };
        }

        // Only create status reference if mapping exists and reference doesn't exist yet
        if (statusMapping && !statusKnx.value[value.valueKey]) {
          const dptString = Array.isArray(statusMapping.knxDpt) ? statusMapping.knxDpt[0] : statusMapping.knxDpt;
          statusKnx.value[value.valueKey] = {
            groupAddress: '',
            dpt: extractDptNumber(dptString),
          };
        }
      });
    } else if (selectedClass.value && integrationType.value === 'matter') {
      // Similar logic for Matter devices
      deviceValuesList.value.forEach((value) => {
        const controlMapping = templateMappings.value.find(
          (m) => m.valueKey === value.valueKey && m.referenceType === 'control',
        );
        const statusMapping = templateMappings.value.find(
          (m) => m.valueKey === value.valueKey && m.referenceType === 'status',
        );

        if (!controlMatter.value[value.valueKey]) {
          controlMatter.value[value.valueKey] = {
            propertyId: '',
            clusterId: controlMapping?.matterClusterId ?? 0,
            attributeId: controlMapping?.matterAttributeId ?? 0,
          };
        }

        if (!statusMatter.value[value.valueKey]) {
          statusMatter.value[value.valueKey] = {
            propertyId: '',
            clusterId: statusMapping?.matterClusterId ?? 0,
            attributeId: statusMapping?.matterAttributeId ?? 0,
          };
        }
      });
    } else if (selectedClass.value && integrationType.value === 'bacnet') {
      deviceValuesList.value.forEach((value) => {
        const controlMapping = templateMappings.value.find(
          (m) => m.valueKey === value.valueKey && m.referenceType === 'control',
        );
        const statusMapping = templateMappings.value.find(
          (m) => m.valueKey === value.valueKey && m.referenceType === 'status',
        );

        if (controlMapping && !controlBacnet.value[value.valueKey]) {
          controlBacnet.value[value.valueKey] = {
            propertyId: '',
            deviceInstance: 0,
            address: '',
            objectType: controlMapping.bacnetObjectType || '',
            objectInstance: 0,
            propertyName: String(controlMapping.bacnetPropertyId || 'PRESENT_VALUE'),
          };
        }

        if (statusMapping && !statusBacnet.value[value.valueKey]) {
          statusBacnet.value[value.valueKey] = {
            propertyId: '',
            deviceInstance: 0,
            address: '',
            objectType: statusMapping.bacnetObjectType || '',
            objectInstance: 0,
            propertyName: String(statusMapping.bacnetPropertyId || 'PRESENT_VALUE'),
          };
        }
      });
    } else if (selectedClass.value && integrationType.value === 'modbus') {
      deviceValuesList.value.forEach((value) => {
        const controlMapping = templateMappings.value.find(
          (m) => m.valueKey === value.valueKey && m.referenceType === 'control',
        );
        const statusMapping = templateMappings.value.find(
          (m) => m.valueKey === value.valueKey && m.referenceType === 'status',
        );

        if (controlMapping && !controlModbus.value[value.valueKey]) {
          controlModbus.value[value.valueKey] = {
            propertyId: '',
            slaveAddress: 0,
            tableType: controlMapping.modbusTableType || '',
            registerAddress: 0,
            dataFormat: controlMapping.modbusDataFormat || '',
          };
        }

        if (statusMapping && !statusModbus.value[value.valueKey]) {
          statusModbus.value[value.valueKey] = {
            propertyId: '',
            slaveAddress: 0,
            tableType: statusMapping.modbusTableType || '',
            registerAddress: 0,
            dataFormat: statusMapping.modbusDataFormat || '',
          };
        }
      });
    }
  }

  // Subscribe to real-time updates
  socketStore.subscribe('device', props.device.id);
  socketStore.on('value_changed', handleValueChanged);
}

async function loadDeviceClass(classId: string) {
  console.log('[DeviceEditor] loadDeviceClass called with:', classId, 'mode:', props.mode);
  isLoadingClass.value = true; // Set guard to prevent watcher infinite loop

  try {
    // Get the full device class details from the store
    const fullClass = await deviceClassesStore.getFullClass(classId, formData.value.settings);

    if (fullClass) {
      console.log('[DeviceEditor] Loaded device class:', classId, fullClass);
      console.log('[DeviceEditor] Class settings:', fullClass.settings);
      console.log('[DeviceEditor] Class values:', fullClass.values);
      console.log('[DeviceEditor] Class mappings:', fullClass.mappings);
      selectedClass.value = fullClass;

      // Store setting definitions for rendering dropdowns
      const settingDefs = fullClass.settings || fullClass.settingDefinitions || [];
      if (Array.isArray(settingDefs)) {
        settingDefinitions.value = {};
        previousEnumValues.value = {};
        settingDefs.forEach((def) => {
          settingDefinitions.value[def.settingKey] = def;
          // Initialize previousEnumValues for ALL setting types to prevent watcher from treating initial load as a change
          previousEnumValues.value[def.settingKey] = formData.value.settings[def.settingKey];
        });
        console.log('[DeviceEditor] Loaded setting definitions:', settingDefinitions.value);
      }

      // In edit mode, just store the class for reference
      if (props.mode === 'edit') {
        // In edit mode, don't override any data - class is just for reference
        return;
      }

      // Only initialize data from class in add mode
      if (props.mode === 'add') {
        console.log('[DeviceEditor] Initializing device from class in add mode');
        // Initialize form data from device class
        formData.value.deviceType = classId;
        formData.value.icon = fullClass.icon;
        formData.value.logicScript = '';

        // Initialize settings from device class defaults
        const settings: Record<string, unknown> = fullClass.defaultSettings || {};
        console.log('[DeviceEditor] Processing device class settings:', Object.keys(settings).length, 'items');

        formData.value.settings = settings;
        deviceSettings.value = JSON.parse(JSON.stringify(settings));

        console.log('[DeviceEditor] Initialized settings from device class:', settings);
        console.log('[DeviceEditor] deviceSettings.value:', deviceSettings.value);

        // Initialize values list (only in add mode)
        if (fullClass.values && props.mode === 'add') {
          console.log('[DeviceEditor] Initializing values from device class:', fullClass.values.length, 'values');
          deviceValuesList.value = fullClass.values.map((val) => ({
            ...val,
            currentValue: null,
          }));
          console.log('[DeviceEditor] deviceValuesList after init:', deviceValuesList.value);
        }
      }
    } else {
      console.error('[DeviceEditor] Failed to load device class:', classId);
    }
  } catch (error) {
    console.error('[DeviceEditor] Error loading device class:', error);
  } finally {
    isLoadingClass.value = false; // Clear guard after loading completes
  }
}

// Reload values and mappings for EDIT mode - merges with existing DB values instead of replacing
async function reloadDeviceValuesWithSettingsEditMode(classId: string) {
  try {
    const fullClass = await deviceClassesStore.getFullClass(classId, deviceSettings.value);
    if (!fullClass?.values) return;

    selectedClass.value = fullClass;

    const extractDptNumber = (dpt: unknown): number => {
      if (typeof dpt === 'number') return dpt;
      if (!dpt) return 1;
      const match = String(dpt).match(/(\d+)(?:\.(\d+))?/);
      return match ? parseInt(match[1]) : 1;
    };

    // Build index of current values by key
    const existingByKey: Record<string, DeviceValueRow> = {};
    deviceValuesList.value.forEach((v) => {
      existingByKey[v.valueKey] = v;
    });

    // Always keep user-created custom values
    const userValues = deviceValuesList.value.filter((v) => v.source === 'user');

    // Build new list: for each class value, keep the existing DB entry or create a new placeholder
    const newValuesList = fullClass.values.map((classVal) => {
      const existing = existingByKey[classVal.valueKey];
      return existing
        ? existing
        : { ...classVal, name: classVal.name ?? '', valueType: classVal.valueType ?? '', currentValue: null };
    });

    deviceValuesList.value = [...newValuesList, ...userValues];

    const newClassKeys = new Set(fullClass.values.map((v) => v.valueKey));

    if (integrationType.value === 'knx' && fullClass.mappings) {
      // Remove references for values no longer in the class definition (non-user)
      for (const refMap of [controlKnx.value, statusKnx.value]) {
        Object.keys(refMap).forEach((key) => {
          const baseKey = key.includes('__') ? key.split('__')[0] : key;
          if (!newClassKeys.has(baseKey) && existingByKey[baseKey]?.source !== 'user') {
            delete refMap[key];
          }
        });
      }
      // Add empty references for new mappings
      fullClass.mappings.forEach((mapping, mappingIndex) => {
        const valueKey = mapping.valueKey;
        const allMappingsForValue = fullClass.mappings.filter((m) => m.valueKey === valueKey);
        let refKey = valueKey;
        if (allMappingsForValue.length > 1) refKey = `${valueKey}__${mappingIndex}`;

        if (mapping.referenceType === 'control' && !controlKnx.value[refKey]) {
          controlKnx.value[refKey] = { groupAddress: '', dpt: extractDptNumber(mapping.knxDpt) };
        } else if (mapping.referenceType === 'status' && !statusKnx.value[refKey]) {
          statusKnx.value[refKey] = { groupAddress: '', dpt: extractDptNumber(mapping.knxDpt) };
        }
      });
    } else if (integrationType.value === 'matter' && fullClass.mappings) {
      for (const refMap of [controlMatter.value, statusMatter.value]) {
        Object.keys(refMap).forEach((key) => {
          if (!newClassKeys.has(key) && existingByKey[key]?.source !== 'user') delete refMap[key];
        });
      }
      fullClass.mappings.forEach((mapping) => {
        const valueKey = mapping.valueKey;
        if (mapping.referenceType === 'control' && !controlMatter.value[valueKey]) {
          controlMatter.value[valueKey] = {
            propertyId: '',
            clusterId: mapping.matterClusterId ?? 0,
            attributeId: mapping.matterAttributeId ?? 0,
          };
        } else if (mapping.referenceType === 'status' && !statusMatter.value[valueKey]) {
          statusMatter.value[valueKey] = {
            propertyId: '',
            clusterId: mapping.matterClusterId ?? 0,
            attributeId: mapping.matterAttributeId ?? 0,
          };
        }
      });
    } else if (integrationType.value === 'bacnet' && fullClass.mappings) {
      for (const refMap of [controlBacnet.value, statusBacnet.value]) {
        Object.keys(refMap).forEach((key) => {
          if (!newClassKeys.has(key) && existingByKey[key]?.source !== 'user') delete refMap[key];
        });
      }
      fullClass.mappings.forEach((mapping) => {
        const valueKey = mapping.valueKey;
        if (mapping.referenceType === 'control' && !controlBacnet.value[valueKey]) {
          controlBacnet.value[valueKey] = {
            propertyId: '',
            deviceInstance: 0,
            address: '',
            objectType: mapping.bacnetObjectType || '',
            objectInstance: 0,
            propertyName: String(mapping.bacnetPropertyId || 'PRESENT_VALUE'),
          };
        } else if (mapping.referenceType === 'status' && !statusBacnet.value[valueKey]) {
          statusBacnet.value[valueKey] = {
            propertyId: '',
            deviceInstance: 0,
            address: '',
            objectType: mapping.bacnetObjectType || '',
            objectInstance: 0,
            propertyName: String(mapping.bacnetPropertyId || 'PRESENT_VALUE'),
          };
        }
      });
    } else if (integrationType.value === 'modbus' && fullClass.mappings) {
      for (const refMap of [controlModbus.value, statusModbus.value]) {
        Object.keys(refMap).forEach((key) => {
          if (!newClassKeys.has(key) && existingByKey[key]?.source !== 'user') delete refMap[key];
        });
      }
      fullClass.mappings.forEach((mapping) => {
        const valueKey = mapping.valueKey;
        if (mapping.referenceType === 'control' && !controlModbus.value[valueKey]) {
          controlModbus.value[valueKey] = {
            propertyId: '',
            slaveAddress: 0,
            tableType: mapping.modbusTableType || '',
            registerAddress: 0,
            dataFormat: mapping.modbusDataFormat || '',
          };
        } else if (mapping.referenceType === 'status' && !statusModbus.value[valueKey]) {
          statusModbus.value[valueKey] = {
            propertyId: '',
            slaveAddress: 0,
            tableType: mapping.modbusTableType || '',
            registerAddress: 0,
            dataFormat: mapping.modbusDataFormat || '',
          };
        }
      });
    }
  } catch (error) {
    console.error('[DeviceEditor] Error reloading device values for edit mode:', error);
  }
}

// Reload values and mappings based on current settings (without resetting the settings themselves)
async function reloadDeviceValuesWithSettings(classId: string) {
  try {
    const fullClass = await deviceClassesStore.getFullClass(classId, deviceSettings.value);

    if (fullClass) {
      console.log('[DeviceEditor] Reloaded device class with updated settings:', fullClass);
      selectedClass.value = fullClass;

      // Update values list to show new values based on updated settings
      if (fullClass.values) {
        console.log('[DeviceEditor] Updating values list with new values:', fullClass.values.length);
        deviceValuesList.value = fullClass.values.map((val) => ({
          ...val,
          currentValue: null,
        }));

        // Clear old references
        controlKnx.value = {};
        statusKnx.value = {};
        controlMatter.value = {};
        statusMatter.value = {};
        controlBacnet.value = {};
        statusBacnet.value = {};
        controlModbus.value = {};
        statusModbus.value = {};

        // Re-initialize references for new mappings with correct DPT
        if (integrationType.value === 'knx' && fullClass.mappings) {
          const extractDptNumber = (dpt: unknown): number => {
            if (typeof dpt === 'number') return dpt;
            if (!dpt) return 1;
            const match = String(dpt).match(/(\d+)(?:\.(\d+))?/);
            return match ? parseInt(match[1]) : 1;
          };

          // For each mapping, create an initial reference entry with the DPT
          fullClass.mappings.forEach((mapping, mappingIndex) => {
            const valueKey = mapping.valueKey;
            const allMappingsForValue = fullClass.mappings.filter((m) => m.valueKey === valueKey);

            // Use composite key if multiple mappings for this value
            let refKey = valueKey;
            if (allMappingsForValue.length > 1) {
              refKey = `${valueKey}__${mappingIndex}`;
            }

            if (mapping.referenceType === 'control') {
              if (!controlKnx.value[refKey]) {
                controlKnx.value[refKey] = {
                  groupAddress: '',
                  dpt: extractDptNumber(mapping.knxDpt),
                };
              }
            } else if (mapping.referenceType === 'status') {
              if (!statusKnx.value[refKey]) {
                statusKnx.value[refKey] = {
                  groupAddress: '',
                  dpt: extractDptNumber(mapping.knxDpt),
                };
              }
            }
          });
        } else if (integrationType.value === 'matter' && fullClass.mappings) {
          // Similar logic for Matter devices
          fullClass.mappings.forEach((mapping, mappingIndex) => {
            const valueKey = mapping.valueKey;
            const allMappingsForValue = fullClass.mappings.filter((m) => m.valueKey === valueKey);

            let refKey = valueKey;
            if (allMappingsForValue.length > 1) {
              refKey = `${valueKey}__${mappingIndex}`;
            }

            if (mapping.referenceType === 'control') {
              if (!controlMatter.value?.[refKey]) {
                controlMatter.value[refKey] = {
                  propertyId: '',
                  clusterId: mapping.matterClusterId ?? 0,
                  attributeId: mapping.matterAttributeId ?? 0,
                };
              }
            } else if (mapping.referenceType === 'status') {
              if (!statusMatter.value?.[refKey]) {
                statusMatter.value[refKey] = {
                  propertyId: '',
                  clusterId: mapping.matterClusterId ?? 0,
                  attributeId: mapping.matterAttributeId ?? 0,
                };
              }
            }
          });
        } else if (integrationType.value === 'bacnet' && fullClass.mappings) {
          fullClass.mappings.forEach((mapping, mappingIndex) => {
            const valueKey = mapping.valueKey;
            const allMappingsForValue = fullClass.mappings.filter((m) => m.valueKey === valueKey);

            let refKey = valueKey;
            if (allMappingsForValue.length > 1) {
              refKey = `${valueKey}__${mappingIndex}`;
            }

            if (mapping.referenceType === 'control') {
              if (!controlBacnet.value?.[refKey]) {
                controlBacnet.value[refKey] = {
                  propertyId: '',
                  deviceInstance: 0,
                  address: '',
                  objectType: mapping.bacnetObjectType || '',
                  objectInstance: 0,
                  propertyName: String(mapping.bacnetPropertyId || 'PRESENT_VALUE'),
                };
              }
            } else if (mapping.referenceType === 'status') {
              if (!statusBacnet.value?.[refKey]) {
                statusBacnet.value[refKey] = {
                  propertyId: '',
                  deviceInstance: 0,
                  address: '',
                  objectType: mapping.bacnetObjectType || '',
                  objectInstance: 0,
                  propertyName: String(mapping.bacnetPropertyId || 'PRESENT_VALUE'),
                };
              }
            }
          });
        } else if (integrationType.value === 'modbus' && fullClass.mappings) {
          fullClass.mappings.forEach((mapping, mappingIndex) => {
            const valueKey = mapping.valueKey;
            const allMappingsForValue = fullClass.mappings.filter((m) => m.valueKey === valueKey);

            let refKey = valueKey;
            if (allMappingsForValue.length > 1) {
              refKey = `${valueKey}__${mappingIndex}`;
            }

            if (mapping.referenceType === 'control') {
              if (!controlModbus.value?.[refKey]) {
                controlModbus.value[refKey] = {
                  propertyId: '',
                  slaveAddress: 0,
                  tableType: mapping.modbusTableType || '',
                  registerAddress: 0,
                  dataFormat: mapping.modbusDataFormat || '',
                };
              }
            } else if (mapping.referenceType === 'status') {
              if (!statusModbus.value?.[refKey]) {
                statusModbus.value[refKey] = {
                  propertyId: '',
                  slaveAddress: 0,
                  tableType: mapping.modbusTableType || '',
                  registerAddress: 0,
                  dataFormat: mapping.modbusDataFormat || '',
                };
              }
            }
          });
        }
      }
    }
  } catch (error) {
    console.error('[DeviceEditor] Error reloading device values:', error);
  }
}

async function onClassChange() {
  console.log('[DeviceEditor] onClassChange called, selectedClassId:', selectedClassId.value);
  if (selectedClassId.value) {
    await loadDeviceClass(selectedClassId.value);

    // Auto-fill device name if empty
    if (!formData.value.deviceName || formData.value.deviceName.trim() === '') {
      if (selectedClass.value?.name) {
        formData.value.deviceName = selectedClass.value.name;
      }
    }
  }
}

async function onEnumSettingChange(key: string) {
  console.log('[DeviceEditor] onEnumSettingChange called for key:', key, 'new value:', deviceSettings.value[key]);

  if (props.mode !== 'add' || !selectedClassId.value) return;

  // Update previousEnumValues to track this change
  previousEnumValues.value[key] = deviceSettings.value[key];

  // Clear all value references when enum setting changes
  // This ensures new values will have clean references without stale mappings
  console.log('[DeviceEditor] Clearing value references due to enum setting change');
  controlKnx.value = {};
  statusKnx.value = {};
  controlMatter.value = {};
  statusMatter.value = {};
  controlBacnet.value = {};
  statusBacnet.value = {};
  controlModbus.value = {};
  statusModbus.value = {};

  // Update formData and reload mappings/values with new settings (preserving the setting change)
  formData.value.settings = JSON.parse(JSON.stringify(deviceSettings.value));

  // Clear class cache to ensure we get fresh mappings for the new settings
  const classesStore = useDeviceClassesStore();
  classesStore.clearCache();

  await reloadDeviceValuesWithSettings(selectedClassId.value);
}

function handleValueChanged(payload: ValueChangedPayload) {
  if (!props.device || payload.deviceId !== props.device.id) return;

  const idx = deviceValuesList.value.findIndex(
    (v) => (payload.valueId && v.id === payload.valueId) || (payload.valueKey && v.valueKey === payload.valueKey),
  );

  if (idx >= 0) {
    const existing = deviceValuesList.value[idx];
    deviceValuesList.value[idx] = {
      ...existing,
      currentValue: wireJsonValue(payload.value),
      lastUpdated: payload.timestamp,
    };
  }
}

function formatSettingLabel(key: string): string {
  return key
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function isSettingVisible(key: string): boolean {
  const def = settingDefinitions.value[key];
  if (!def) return true;

  // Per-integration visibility (e.g. knx_dpt only for KNX devices)
  if (
    def.visibleForIntegrations &&
    Array.isArray(def.visibleForIntegrations) &&
    def.visibleForIntegrations.length > 0
  ) {
    const it = (integrationType.value || '').toLowerCase();
    if (!def.visibleForIntegrations.includes(it)) return false;
  }

  if (!def?.visibleWhen) return true;
  const { settingKey, operator, value } = def.visibleWhen;
  const currentValue = deviceSettings.value[settingKey];
  switch (operator) {
    case '==':
      return currentValue === value;
    case '!=':
      return currentValue !== value;
    case 'in':
    case 'not_in': {
      if (!Array.isArray(value)) return false;
      const candidates: unknown[] = value;
      const found = candidates.includes(currentValue);
      return operator === 'in' ? found : !found;
    }
    case '>':
    case '<':
    case '>=':
    case '<=': {
      const current = settingOperand(currentValue);
      const declared = settingOperand(value);
      if (current === undefined || declared === undefined) return false;
      switch (operator) {
        case '>':
          return current > declared;
        case '<':
          return current < declared;
        case '>=':
          return current >= declared;
        default:
          return current <= declared;
      }
    }
    default:
      return true;
  }
}

function ensureNumber(key: string) {
  // Ensure numeric settings stay as numbers even if input is cleared
  if (
    typeof deviceSettings.value[key] === 'undefined' ||
    deviceSettings.value[key] === '' ||
    deviceSettings.value[key] === null
  ) {
    deviceSettings.value[key] = 0;
  }
}

// Public methods exposed to parent
function validate(): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  if (!formData.value.deviceName?.trim()) {
    errors.push(tt('Device name is required', 'Ime naprave je obvezno'));
  }

  if (!selectedClassId.value) {
    errors.push(tt('Device type is required', 'Tip naprave je obvezen'));
  }

  // Some add flows allow creating the device first and wiring references later.
  if (props.mode === 'add' && !props.allowEmptyReferences) {
    if (integrationType.value === 'knx') {
      const hasControl = Object.values(controlKnx.value).some(
        (config) => config.groupAddress && config.groupAddress.trim() !== '',
      );
      const hasStatus = Object.values(statusKnx.value).some(
        (config) => config.groupAddress && config.groupAddress.trim() !== '',
      );
      if (!hasControl && !hasStatus) {
        errors.push(
          tt(
            'At least one reference (control or status) is required',
            'Potrebna je vsaj ena referenca (upravljanje ali stanje)',
          ),
        );
      }
    } else if (integrationType.value === 'modbus') {
      const hasControl = Object.values(controlModbus.value).some(
        (config) => config.propertyId && config.propertyId.trim() !== '',
      );
      const hasStatus = Object.values(statusModbus.value).some(
        (config) => config.propertyId && config.propertyId.trim() !== '',
      );
      if (!hasControl && !hasStatus) {
        errors.push(
          tt(
            'At least one reference (control or status) is required',
            'Potrebna je vsaj ena referenca (upravljanje ali stanje)',
          ),
        );
      }
    } else if (integrationType.value === 'mqtt') {
      const hasControl = Object.values(controlMqtt.value).some(
        (config) => config.propertyId && config.propertyId.trim() !== '',
      );
      const hasStatus = Object.values(statusMqtt.value).some(
        (config) => config.propertyId && config.propertyId.trim() !== '',
      );
      if (!hasControl && !hasStatus) {
        errors.push(
          tt(
            'At least one reference (control or status) is required',
            'Potrebna je vsaj ena referenca (upravljanje ali stanje)',
          ),
        );
      }
    } else if (integrationType.value === 'bacnet') {
      const hasControl = Object.values(controlBacnet.value).some(
        (config) => config.propertyId && config.propertyId.trim() !== '',
      );
      const hasStatus = Object.values(statusBacnet.value).some(
        (config) => config.propertyId && config.propertyId.trim() !== '',
      );
      if (!hasControl && !hasStatus) {
        errors.push(
          tt(
            'At least one reference (control or status) is required',
            'Potrebna je vsaj ena referenca (upravljanje ali stanje)',
          ),
        );
      }
    } else if (integrationType.value === 'matter') {
      const hasControl = Object.values(controlMatter.value).some(
        (config) => config.propertyId && config.propertyId.trim() !== '',
      );
      const hasStatus = Object.values(statusMatter.value).some(
        (config) => config.propertyId && config.propertyId.trim() !== '',
      );
      if (!hasControl && !hasStatus) {
        errors.push(
          tt(
            'At least one reference (control or status) is required',
            'Potrebna je vsaj ena referenca (upravljanje ali stanje)',
          ),
        );
      }
    } else if (integrationType.value === 'esphome') {
      const hasControl = Object.values(controlESPHome.value).some(
        (config) => config.esphomeEntityId && config.esphomeEntityId.trim() !== '',
      );
      const hasStatus = Object.values(statusESPHome.value).some(
        (config) => config.esphomeEntityId && config.esphomeEntityId.trim() !== '',
      );
      if (!hasControl && !hasStatus) {
        errors.push(
          tt(
            'At least one reference (control or status) is required',
            'Potrebna je vsaj ena referenca (upravljanje ali stanje)',
          ),
        );
      }
    }
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

function getData(): DevicePayload {
  // Give a pre-setting device its kind, once, from its legacy icon/name.
  seedDrawingSettings();

  // Sync deviceSettings back to formData with deep copy
  formData.value.settings = JSON.parse(JSON.stringify(deviceSettings.value));

  const payload: DevicePayload = {
    ...formData.value,
    settings: JSON.parse(JSON.stringify(deviceSettings.value)),
  };

  // The branch that applies fills this list and hangs it off the payload. It is a
  // local because `payload.datapoints` is a property access and its narrowing does
  // not survive into the callbacks below.
  const datapoints: DatapointPayload[] = [];

  // Use classId instead of templateId (for DeviceClass compatibility)
  if (formData.value.deviceType) {
    payload.classId = formData.value.deviceType;
  }
  delete payload.templateId; // Remove templateId from payload

  console.log('[DeviceEditor] getData() - Settings:', payload.settings);
  console.log('[DeviceEditor] getData() - classId:', payload.classId);
  console.log('[DeviceEditor] getData() - selectedClass:', selectedClass.value);

  console.log('[DeviceEditor] getData() - Building payload');
  console.log('[DeviceEditor] Class values:', selectedClass.value?.values);
  console.log('[DeviceEditor] deviceValuesList:', deviceValuesList.value);

  // Build datapoints for KNX/BACnet/Modbus/Matter
  if (integrationType.value === 'knx' && selectedClass.value?.values && selectedClass.value?.mappings) {
    payload.datapoints = datapoints;

    // Get all mappings for reference (filtered to this device's protocol)
    const allMappings = (selectedClass.value.mappings || []).filter((m) => !m.protocolType || m.protocolType === 'knx');

    selectedClass.value.values.forEach((val) => {
      // Find all mappings for this value
      const mappingsForValue = allMappings.filter((m) => m.valueKey === val.valueKey);

      if (mappingsForValue.length > 0) {
        // Use composite keys when there are mappings (DeviceValuesEditor always uses composite keys)
        mappingsForValue.forEach((mapping, mappingIndex) => {
          const compositeKey = `${val.valueKey}__${mappingIndex}`;

          // Try composite key first
          let config =
            mapping.referenceType === 'control' ? controlKnx.value[compositeKey] : statusKnx.value[compositeKey];

          // Fallback to simple key for backward compatibility
          if (!config || (!config.groupAddress && compositeKey.includes('__'))) {
            config =
              mapping.referenceType === 'control' ? controlKnx.value[val.valueKey] : statusKnx.value[val.valueKey];
          }

          if (config?.groupAddress && config.groupAddress.trim()) {
            datapoints.push({
              property_key: val.valueKey,
              config: {
                groupAddress: config.groupAddress,
                dpt: config.dpt,
                role: mapping.referenceType,
                knxRole: mapping.knxRole,
                transformer: mapping.transformer, // Include transformer for this mapping
              },
            });
          }
        });
      } else {
        // No mappings - use simple valueKey format (backward compatible)
        const control = controlKnx.value[val.valueKey];
        const status = statusKnx.value[val.valueKey];

        // Add control datapoint if configured
        if (control?.groupAddress && control.groupAddress.trim()) {
          datapoints.push({
            property_key: val.valueKey,
            config: {
              groupAddress: control.groupAddress,
              dpt: control.dpt,
              role: 'control',
            },
          });
        }

        // Add status datapoint if configured
        if (status?.groupAddress && status.groupAddress.trim()) {
          datapoints.push({
            property_key: val.valueKey,
            config: {
              groupAddress: status.groupAddress,
              dpt: status.dpt,
              role: 'status',
            },
          });
        }
      }
    });

    console.log('[DeviceEditor] Built KNX datapoints:', payload.datapoints);
    console.log('[DeviceEditor] Datapoints count:', payload.datapoints.length);
  } else if (integrationType.value === 'knx' && selectedClass.value?.values) {
    // Fallback for when mappings are not available
    payload.datapoints = datapoints;

    selectedClass.value.values.forEach((val) => {
      const control = controlKnx.value[val.valueKey];
      const status = statusKnx.value[val.valueKey];

      // Add control datapoint if configured
      if (control?.groupAddress && control.groupAddress.trim()) {
        datapoints.push({
          property_key: val.valueKey,
          config: {
            groupAddress: control.groupAddress,
            dpt: control.dpt,
            role: 'control',
          },
        });
      }

      // Add status datapoint if configured
      if (status?.groupAddress && status.groupAddress.trim()) {
        datapoints.push({
          property_key: val.valueKey,
          config: {
            groupAddress: status.groupAddress,
            dpt: status.dpt,
            role: 'status',
          },
        });
      }
    });

    console.log('[DeviceEditor] Built KNX datapoints:', payload.datapoints);
    console.log('[DeviceEditor] Datapoints count:', payload.datapoints.length);
  } else if (integrationType.value === 'modbus' && selectedClass.value?.values && selectedClass.value?.mappings) {
    payload.datapoints = datapoints;

    const allMappings = (selectedClass.value.mappings || []).filter(
      (m) => !m.protocolType || m.protocolType === 'modbus',
    );

    selectedClass.value.values.forEach((val) => {
      const mappingsForValue = allMappings.filter((m) => m.valueKey === val.valueKey);

      if (mappingsForValue.length > 0) {
        mappingsForValue.forEach((mapping, mappingIndex) => {
          const compositeKey = `${val.valueKey}__${mappingIndex}`;

          let config =
            mapping.referenceType === 'control' ? controlModbus.value[compositeKey] : statusModbus.value[compositeKey];

          if (!config || (!config.propertyId && compositeKey.includes('__'))) {
            config =
              mapping.referenceType === 'control'
                ? controlModbus.value[val.valueKey]
                : statusModbus.value[val.valueKey];
          }

          if (config?.propertyId && config.propertyId.trim()) {
            datapoints.push({
              property_key: val.valueKey,
              propertyType: 'modbus',
              propertyId: config.propertyId,
              config: {
                role: mapping.referenceType,
              },
            });
          }
        });
      } else {
        const control = controlModbus.value[val.valueKey];
        const status = statusModbus.value[val.valueKey];

        if (control?.propertyId && control.propertyId.trim()) {
          datapoints.push({
            property_key: val.valueKey,
            propertyType: 'modbus',
            propertyId: control.propertyId,
            config: {
              role: 'control',
            },
          });
        }

        if (status?.propertyId && status.propertyId.trim()) {
          datapoints.push({
            property_key: val.valueKey,
            propertyType: 'modbus',
            propertyId: status.propertyId,
            config: {
              role: 'status',
            },
          });
        }
      }
    });
  } else if (integrationType.value === 'modbus' && selectedClass.value?.values) {
    payload.datapoints = datapoints;

    selectedClass.value.values.forEach((val) => {
      const control = controlModbus.value[val.valueKey];
      const status = statusModbus.value[val.valueKey];

      if (control?.propertyId && control.propertyId.trim()) {
        datapoints.push({
          property_key: val.valueKey,
          propertyType: 'modbus',
          propertyId: control.propertyId,
          config: {
            role: 'control',
          },
        });
      }

      if (status?.propertyId && status.propertyId.trim()) {
        datapoints.push({
          property_key: val.valueKey,
          propertyType: 'modbus',
          propertyId: status.propertyId,
          config: {
            role: 'status',
          },
        });
      }
    });
  } else if (integrationType.value === 'mqtt' && selectedClass.value?.values && selectedClass.value?.mappings) {
    payload.datapoints = datapoints;

    const allMappings = (selectedClass.value.mappings || []).filter(
      (m) => !m.protocolType || m.protocolType === 'mqtt',
    );

    selectedClass.value.values.forEach((val) => {
      const mappingsForValue = allMappings.filter((m) => m.valueKey === val.valueKey);

      if (mappingsForValue.length > 0) {
        mappingsForValue.forEach((mapping, mappingIndex) => {
          const compositeKey = `${val.valueKey}__${mappingIndex}`;

          let config =
            mapping.referenceType === 'control' ? controlMqtt.value[compositeKey] : statusMqtt.value[compositeKey];

          if (!config || (!config.propertyId && compositeKey.includes('__'))) {
            config =
              mapping.referenceType === 'control' ? controlMqtt.value[val.valueKey] : statusMqtt.value[val.valueKey];
          }

          if (config?.propertyId && config.propertyId.trim()) {
            datapoints.push({
              property_key: val.valueKey,
              propertyType: 'mqtt',
              propertyId: config.propertyId,
              config: {
                role: mapping.referenceType,
              },
            });
          }
        });
      } else {
        const control = controlMqtt.value[val.valueKey];
        const status = statusMqtt.value[val.valueKey];

        if (control?.propertyId && control.propertyId.trim()) {
          datapoints.push({
            property_key: val.valueKey,
            propertyType: 'mqtt',
            propertyId: control.propertyId,
            config: {
              role: 'control',
            },
          });
        }

        if (status?.propertyId && status.propertyId.trim()) {
          datapoints.push({
            property_key: val.valueKey,
            propertyType: 'mqtt',
            propertyId: status.propertyId,
            config: {
              role: 'status',
            },
          });
        }
      }
    });
  } else if (integrationType.value === 'mqtt' && selectedClass.value?.values) {
    payload.datapoints = datapoints;

    selectedClass.value.values.forEach((val) => {
      const control = controlMqtt.value[val.valueKey];
      const status = statusMqtt.value[val.valueKey];

      if (control?.propertyId && control.propertyId.trim()) {
        datapoints.push({
          property_key: val.valueKey,
          propertyType: 'mqtt',
          propertyId: control.propertyId,
          config: {
            role: 'control',
          },
        });
      }

      if (status?.propertyId && status.propertyId.trim()) {
        datapoints.push({
          property_key: val.valueKey,
          propertyType: 'mqtt',
          propertyId: status.propertyId,
          config: {
            role: 'status',
          },
        });
      }
    });
  } else if (integrationType.value === 'bacnet' && selectedClass.value?.values && selectedClass.value?.mappings) {
    payload.datapoints = datapoints;

    const allMappings = (selectedClass.value.mappings || []).filter(
      (m) => !m.protocolType || m.protocolType === 'bacnet',
    );

    selectedClass.value.values.forEach((val) => {
      const mappingsForValue = allMappings.filter((m) => m.valueKey === val.valueKey);

      if (mappingsForValue.length > 0) {
        mappingsForValue.forEach((mapping, mappingIndex) => {
          const compositeKey = `${val.valueKey}__${mappingIndex}`;

          let config =
            mapping.referenceType === 'control' ? controlBacnet.value[compositeKey] : statusBacnet.value[compositeKey];

          if (!config || (!config.propertyId && compositeKey.includes('__'))) {
            config =
              mapping.referenceType === 'control'
                ? controlBacnet.value[val.valueKey]
                : statusBacnet.value[val.valueKey];
          }

          if (config?.propertyId && config.propertyId.trim()) {
            datapoints.push({
              property_key: val.valueKey,
              propertyType: 'bacnet',
              propertyId: config.propertyId,
              config: {
                role: mapping.referenceType,
              },
            });
          }
        });
      } else {
        const control = controlBacnet.value[val.valueKey];
        const status = statusBacnet.value[val.valueKey];

        if (control?.propertyId && control.propertyId.trim()) {
          datapoints.push({
            property_key: val.valueKey,
            propertyType: 'bacnet',
            propertyId: control.propertyId,
            config: {
              role: 'control',
            },
          });
        }

        if (status?.propertyId && status.propertyId.trim()) {
          datapoints.push({
            property_key: val.valueKey,
            propertyType: 'bacnet',
            propertyId: status.propertyId,
            config: {
              role: 'status',
            },
          });
        }
      }
    });
  } else if (integrationType.value === 'bacnet' && selectedClass.value?.values) {
    payload.datapoints = datapoints;

    selectedClass.value.values.forEach((val) => {
      const control = controlBacnet.value[val.valueKey];
      const status = statusBacnet.value[val.valueKey];

      if (control?.propertyId && control.propertyId.trim()) {
        datapoints.push({
          property_key: val.valueKey,
          propertyType: 'bacnet',
          propertyId: control.propertyId,
          config: {
            role: 'control',
          },
        });
      }

      if (status?.propertyId && status.propertyId.trim()) {
        datapoints.push({
          property_key: val.valueKey,
          propertyType: 'bacnet',
          propertyId: status.propertyId,
          config: {
            role: 'status',
          },
        });
      }
    });
  } else if (integrationType.value === 'matter' && selectedClass.value?.values && selectedClass.value?.mappings) {
    payload.datapoints = datapoints;

    // Get all mappings for reference (filtered to this device's protocol)
    const allMappings = (selectedClass.value.mappings || []).filter(
      (m) => !m.protocolType || m.protocolType === 'matter',
    );

    selectedClass.value.values.forEach((val) => {
      // Find all mappings for this value
      const mappingsForValue = allMappings.filter((m) => m.valueKey === val.valueKey);

      if (mappingsForValue.length > 0) {
        // Use composite keys when there are mappings (DeviceValuesEditor always uses composite keys)
        mappingsForValue.forEach((mapping, mappingIndex) => {
          const compositeKey = `${val.valueKey}__${mappingIndex}`;

          // Try composite key first
          let config =
            mapping.referenceType === 'control'
              ? controlMatter.value?.[compositeKey]
              : statusMatter.value?.[compositeKey];

          // Fallback to simple key for backward compatibility
          if (!config || (!config.propertyId && compositeKey.includes('__'))) {
            config =
              mapping.referenceType === 'control'
                ? controlMatter.value?.[val.valueKey]
                : statusMatter.value?.[val.valueKey];
          }

          if (config?.propertyId && config.propertyId.trim()) {
            datapoints.push({
              property_key: val.valueKey,
              propertyType: 'matter',
              propertyId: config.propertyId,
              config: {
                clusterId: config.clusterId,
                attributeId: config.attributeId,
                role: mapping.referenceType,
              },
            });
          }
        });
      } else {
        // No mappings - use simple valueKey format (backward compatible)
        const control = controlMatter.value?.[val.valueKey];
        const status = statusMatter.value?.[val.valueKey];

        // Add control datapoint if configured
        if (control?.propertyId && control.propertyId.trim()) {
          datapoints.push({
            property_key: val.valueKey,
            propertyType: 'matter',
            propertyId: control.propertyId,
            config: {
              clusterId: control.clusterId,
              attributeId: control.attributeId,
              role: 'control',
            },
          });
        }

        // Add status datapoint if configured
        if (status?.propertyId && status.propertyId.trim()) {
          datapoints.push({
            property_key: val.valueKey,
            propertyType: 'matter',
            propertyId: status.propertyId,
            config: {
              clusterId: status.clusterId,
              attributeId: status.attributeId,
              role: 'status',
            },
          });
        }
      }
    });
  } else if (integrationType.value === 'matter' && selectedClass.value?.values) {
    // Fallback for when mappings are not available
    payload.datapoints = datapoints;

    selectedClass.value.values.forEach((val) => {
      const control = controlMatter.value?.[val.valueKey];
      const status = statusMatter.value?.[val.valueKey];

      // Add control datapoint if configured
      if (control?.propertyId && control.propertyId.trim()) {
        datapoints.push({
          property_key: val.valueKey,
          propertyType: 'matter',
          propertyId: control.propertyId,
          config: {
            clusterId: control.clusterId,
            attributeId: control.attributeId,
            role: 'control',
          },
        });
      }

      // Add status datapoint if configured
      if (status?.propertyId && status.propertyId.trim()) {
        datapoints.push({
          property_key: val.valueKey,
          propertyType: 'matter',
          propertyId: status.propertyId,
          config: {
            clusterId: status.clusterId,
            attributeId: status.attributeId,
            role: 'status',
          },
        });
      }
    });
  } else if (integrationType.value === 'esphome' && selectedClass.value?.values) {
    payload.datapoints = datapoints;

    selectedClass.value.values.forEach((val) => {
      const control = controlESPHome.value[val.valueKey];
      const status = statusESPHome.value[val.valueKey];

      if (control?.esphomeEntityId && control.esphomeEntityId.trim()) {
        datapoints.push({
          property_key: val.valueKey,
          propertyType: 'esphome',
          propertyId: control.esphomeEntityId,
          entityKey: control.entityKey,
          config: {
            role: 'control',
            entityKey: control.entityKey,
          },
        });
      }

      if (status?.esphomeEntityId && status.esphomeEntityId.trim()) {
        datapoints.push({
          property_key: val.valueKey,
          propertyType: 'esphome',
          propertyId: status.esphomeEntityId,
          entityKey: status.entityKey,
          config: {
            role: 'status',
            entityKey: status.entityKey,
            transformerConfig: buildESPHomeTransformerConfig({}, status.field),
          },
        });
      }
    });
  } else if (integrationType.value === 'zwave' && selectedClass.value?.values) {
    payload.datapoints = datapoints;

    selectedClass.value.values.forEach((val) => {
      const control = controlZWave.value[val.valueKey];
      const status = statusZWave.value[val.valueKey];

      if (control?.zwaveValueId && control.zwaveValueId.trim()) {
        datapoints.push({
          property_key: val.valueKey,
          propertyType: 'zwave',
          zwaveValueId: control.zwaveValueId,
          config: {
            role: 'control',
            zwaveEndpointId: control.zwaveEndpointId || undefined,
          },
        });
      }

      if (status?.zwaveValueId && status.zwaveValueId.trim()) {
        datapoints.push({
          property_key: val.valueKey,
          propertyType: 'zwave',
          zwaveValueId: status.zwaveValueId,
          config: {
            role: 'status',
            zwaveEndpointId: status.zwaveEndpointId || undefined,
          },
        });
      }
    });
  } else if (integrationType.value === 'zigbee' && selectedClass.value?.values) {
    payload.datapoints = datapoints;

    selectedClass.value.values.forEach((val) => {
      const control = controlZigbee.value[val.valueKey];
      const status = statusZigbee.value[val.valueKey];

      if (control?.zigbeeAttributeId && control.zigbeeAttributeId.trim()) {
        datapoints.push({
          property_key: val.valueKey,
          propertyType: 'zigbee',
          zigbeeAttributeId: control.zigbeeAttributeId,
          config: {
            role: 'control',
            zigbeeEndpointId: control.zigbeeEndpointId || undefined,
          },
        });
      }

      if (status?.zigbeeAttributeId && status.zigbeeAttributeId.trim()) {
        datapoints.push({
          property_key: val.valueKey,
          propertyType: 'zigbee',
          zigbeeAttributeId: status.zigbeeAttributeId,
          config: {
            role: 'status',
            zigbeeEndpointId: status.zigbeeEndpointId || undefined,
          },
        });
      }
    });
  } else if (integrationType.value === 'homeassistant' && selectedClass.value?.values) {
    payload.datapoints = datapoints;

    selectedClass.value.values.forEach((val) => {
      const control = controlHA.value[val.valueKey];
      const status = statusHA.value[val.valueKey];

      if (control?.haEntityId && control.haEntityId.trim()) {
        datapoints.push({
          property_key: val.valueKey,
          propertyType: 'homeassistant',
          haEntityId: control.haEntityId,
          config: {
            role: 'control',
            transformerConfig: buildHATransformerConfig(control),
          },
        });
      }

      if (status?.haEntityId && status.haEntityId.trim()) {
        datapoints.push({
          property_key: val.valueKey,
          propertyType: 'homeassistant',
          haEntityId: status.haEntityId,
          config: {
            role: 'status',
            transformerConfig: buildHATransformerConfig(status),
          },
        });
      }
    });
  }

  return payload;
}

// Save references in edit mode
async function saveReferences(): Promise<boolean> {
  if (props.mode !== 'edit' || !props.device) return true;

  const errors: string[] = [];

  console.log('[DeviceEditor] saveReferences - controlKnx:', controlKnx.value);
  console.log('[DeviceEditor] saveReferences - statusKnx:', statusKnx.value);
  console.log('[DeviceEditor] saveReferences - deviceValuesList:', deviceValuesList.value);
  console.log('[DeviceEditor] saveReferences - templateMappings:', templateMappings.value);

  // Get all mappings for reference
  const allMappings = templateMappings.value || [];

  // Update each device value's references
  for (const value of deviceValuesList.value) {
    if (value.source === 'user') {
      continue;
    }

    // Skip values without a DB id (placeholder entries not yet persisted)
    if (!value.id) {
      console.warn(`[DeviceEditor] Skipping value '${value.valueKey}' - no database id (not yet persisted)`);
      continue;
    }
    try {
      // Find all mappings for this value
      const mappingsForValue = allMappings.filter((m) => m.valueKey === value.valueKey);

      console.log(`[DeviceEditor] Processing value ${value.valueKey}:`, {
        mappingsCount: mappingsForValue.length,
        mappings: mappingsForValue,
      });

      let knxControl: KNXConfig | null = null;
      let knxStatus: KNXConfig | null = null;
      let modbusControl: ModbusConfig | null = null;
      let modbusStatus: ModbusConfig | null = null;
      let mqttControl: MQTTConfig | null = null;
      let mqttStatus: MQTTConfig | null = null;
      let bacnetControl: BacnetConfig | null = null;
      let bacnetStatus: BacnetConfig | null = null;
      let matterControl: MatterConfig | null = null;
      let matterStatus: MatterConfig | null = null;
      let esphomeControl: ESPHomeEntityRef | null = null;
      let esphomeStatus: ESPHomeEntityRef | null = null;
      let zwaveControl: ZWaveValueRef | null = null;
      let zwaveStatus: ZWaveValueRef | null = null;
      let zigbeeControl: ZigbeeAttributeRef | null = null;
      let zigbeeStatus: ZigbeeAttributeRef | null = null;
      let haControl: HAEntityRef | null = null;
      let haStatus: HAEntityRef | null = null;

      // If there are mappings, check for composite keys first
      if (mappingsForValue.length > 0) {
        // Use composite keys when there are mappings
        mappingsForValue.forEach((mapping, mappingIndex) => {
          const compositeKey = `${value.valueKey}__${mappingIndex}`;

          if (mapping.referenceType === 'control') {
            if (integrationType.value === 'knx' && controlKnx.value[compositeKey]) {
              knxControl = controlKnx.value[compositeKey];
              console.log(`[DeviceEditor] Found KNX control at composite key ${compositeKey}:`, knxControl);
            } else if (integrationType.value === 'bacnet' && controlBacnet.value[compositeKey]) {
              bacnetControl = controlBacnet.value[compositeKey];
              console.log(`[DeviceEditor] Found BACnet control at composite key ${compositeKey}:`, bacnetControl);
            } else if (integrationType.value === 'modbus' && controlModbus.value[compositeKey]) {
              modbusControl = controlModbus.value[compositeKey];
              console.log(`[DeviceEditor] Found Modbus control at composite key ${compositeKey}:`, modbusControl);
            } else if (integrationType.value === 'mqtt' && controlMqtt.value[compositeKey]) {
              mqttControl = controlMqtt.value[compositeKey];
              console.log(`[DeviceEditor] Found MQTT control at composite key ${compositeKey}:`, mqttControl);
            } else if (integrationType.value === 'matter' && controlMatter.value[compositeKey]) {
              matterControl = controlMatter.value[compositeKey];
              console.log(`[DeviceEditor] Found Matter control at composite key ${compositeKey}:`, matterControl);
            } else if (integrationType.value === 'esphome' && controlESPHome.value[compositeKey]) {
              esphomeControl = controlESPHome.value[compositeKey];
              console.log(`[DeviceEditor] Found ESPHome control at composite key ${compositeKey}:`, esphomeControl);
            } else if (integrationType.value === 'zwave' && controlZWave.value[compositeKey]) {
              zwaveControl = controlZWave.value[compositeKey];
              console.log(`[DeviceEditor] Found Z-Wave control at composite key ${compositeKey}:`, zwaveControl);
            } else if (integrationType.value === 'zigbee' && controlZigbee.value[compositeKey]) {
              zigbeeControl = controlZigbee.value[compositeKey];
              console.log(`[DeviceEditor] Found Zigbee control at composite key ${compositeKey}:`, zigbeeControl);
            } else if (integrationType.value === 'homeassistant' && controlHA.value[compositeKey]) {
              haControl = controlHA.value[compositeKey];
              console.log(`[DeviceEditor] Found HA control at composite key ${compositeKey}:`, haControl);
            }
          } else if (mapping.referenceType === 'status') {
            if (integrationType.value === 'knx' && statusKnx.value[compositeKey]) {
              knxStatus = statusKnx.value[compositeKey];
              console.log(`[DeviceEditor] Found KNX status at composite key ${compositeKey}:`, knxStatus);
            } else if (integrationType.value === 'bacnet' && statusBacnet.value[compositeKey]) {
              bacnetStatus = statusBacnet.value[compositeKey];
              console.log(`[DeviceEditor] Found BACnet status at composite key ${compositeKey}:`, bacnetStatus);
            } else if (integrationType.value === 'modbus' && statusModbus.value[compositeKey]) {
              modbusStatus = statusModbus.value[compositeKey];
              console.log(`[DeviceEditor] Found Modbus status at composite key ${compositeKey}:`, modbusStatus);
            } else if (integrationType.value === 'mqtt' && statusMqtt.value[compositeKey]) {
              mqttStatus = statusMqtt.value[compositeKey];
              console.log(`[DeviceEditor] Found MQTT status at composite key ${compositeKey}:`, mqttStatus);
            } else if (integrationType.value === 'matter' && statusMatter.value[compositeKey]) {
              matterStatus = statusMatter.value[compositeKey];
              console.log(`[DeviceEditor] Found Matter status at composite key ${compositeKey}:`, matterStatus);
            } else if (integrationType.value === 'esphome' && statusESPHome.value[compositeKey]) {
              esphomeStatus = statusESPHome.value[compositeKey];
              console.log(`[DeviceEditor] Found ESPHome status at composite key ${compositeKey}:`, esphomeStatus);
            } else if (integrationType.value === 'zwave' && statusZWave.value[compositeKey]) {
              zwaveStatus = statusZWave.value[compositeKey];
              console.log(`[DeviceEditor] Found Z-Wave status at composite key ${compositeKey}:`, zwaveStatus);
            } else if (integrationType.value === 'zigbee' && statusZigbee.value[compositeKey]) {
              zigbeeStatus = statusZigbee.value[compositeKey];
              console.log(`[DeviceEditor] Found Zigbee status at composite key ${compositeKey}:`, zigbeeStatus);
            } else if (integrationType.value === 'homeassistant' && statusHA.value[compositeKey]) {
              haStatus = statusHA.value[compositeKey];
              console.log(`[DeviceEditor] Found HA status at composite key ${compositeKey}:`, haStatus);
            }
          }
        });
      }

      // Fallback to simple keys if composite keys not found (backward compatibility)
      if (!knxControl && integrationType.value === 'knx') {
        knxControl = controlKnx.value[value.valueKey];
        if (knxControl) console.log(`[DeviceEditor] Using simple key for KNX control:`, knxControl);
      }
      if (!knxStatus && integrationType.value === 'knx') {
        knxStatus = statusKnx.value[value.valueKey];
        if (knxStatus) console.log(`[DeviceEditor] Using simple key for KNX status:`, knxStatus);
      }
      if (!bacnetControl && integrationType.value === 'bacnet') {
        bacnetControl = controlBacnet.value[value.valueKey];
        if (bacnetControl) console.log(`[DeviceEditor] Using simple key for BACnet control:`, bacnetControl);
      }
      if (!bacnetStatus && integrationType.value === 'bacnet') {
        bacnetStatus = statusBacnet.value[value.valueKey];
        if (bacnetStatus) console.log(`[DeviceEditor] Using simple key for BACnet status:`, bacnetStatus);
      }
      if (!modbusControl && integrationType.value === 'modbus') {
        modbusControl = controlModbus.value[value.valueKey];
        if (modbusControl) console.log(`[DeviceEditor] Using simple key for Modbus control:`, modbusControl);
      }
      if (!modbusStatus && integrationType.value === 'modbus') {
        modbusStatus = statusModbus.value[value.valueKey];
        if (modbusStatus) console.log(`[DeviceEditor] Using simple key for Modbus status:`, modbusStatus);
      }
      if (!mqttControl && integrationType.value === 'mqtt') {
        mqttControl = controlMqtt.value[value.valueKey];
        if (mqttControl) console.log(`[DeviceEditor] Using simple key for MQTT control:`, mqttControl);
      }
      if (!mqttStatus && integrationType.value === 'mqtt') {
        mqttStatus = statusMqtt.value[value.valueKey];
        if (mqttStatus) console.log(`[DeviceEditor] Using simple key for MQTT status:`, mqttStatus);
      }
      if (!matterControl && integrationType.value === 'matter') {
        matterControl = controlMatter.value[value.valueKey];
        if (matterControl) console.log(`[DeviceEditor] Using simple key for Matter control:`, matterControl);
      }
      if (!matterStatus && integrationType.value === 'matter') {
        matterStatus = statusMatter.value[value.valueKey];
        if (matterStatus) console.log(`[DeviceEditor] Using simple key for Matter status:`, matterStatus);
      }
      if (!esphomeControl && integrationType.value === 'esphome') {
        esphomeControl = controlESPHome.value[value.valueKey];
        if (esphomeControl) console.log(`[DeviceEditor] Using simple key for ESPHome control:`, esphomeControl);
      }
      if (!esphomeStatus && integrationType.value === 'esphome') {
        esphomeStatus = statusESPHome.value[value.valueKey];
        if (esphomeStatus) console.log(`[DeviceEditor] Using simple key for ESPHome status:`, esphomeStatus);
      }
      if (!zwaveControl && integrationType.value === 'zwave') {
        zwaveControl = controlZWave.value[value.valueKey];
        if (zwaveControl) console.log(`[DeviceEditor] Using simple key for Z-Wave control:`, zwaveControl);
      }
      if (!zwaveStatus && integrationType.value === 'zwave') {
        zwaveStatus = statusZWave.value[value.valueKey];
        if (zwaveStatus) console.log(`[DeviceEditor] Using simple key for Z-Wave status:`, zwaveStatus);
      }
      if (!zigbeeControl && integrationType.value === 'zigbee') {
        zigbeeControl = controlZigbee.value[value.valueKey];
        if (zigbeeControl) console.log(`[DeviceEditor] Using simple key for Zigbee control:`, zigbeeControl);
      }
      if (!zigbeeStatus && integrationType.value === 'zigbee') {
        zigbeeStatus = statusZigbee.value[value.valueKey];
        if (zigbeeStatus) console.log(`[DeviceEditor] Using simple key for Zigbee status:`, zigbeeStatus);
      }
      if (!haControl && integrationType.value === 'homeassistant') {
        haControl = controlHA.value[value.valueKey];
        if (haControl) console.log(`[DeviceEditor] Using simple key for HA control:`, haControl);
      }
      if (!haStatus && integrationType.value === 'homeassistant') {
        haStatus = statusHA.value[value.valueKey];
        if (haStatus) console.log(`[DeviceEditor] Using simple key for HA status:`, haStatus);
      }

      const payload: ReferenceUpdate = {};

      // Build control reference
      if (integrationType.value === 'knx') {
        // Always include control
        payload.control = {
          propertyType: 'knx',
          groupAddress: (knxControl?.groupAddress || '').trim(),
          dpt: knxControl?.dpt || 1,
        };
      } else if (integrationType.value === 'bacnet') {
        payload.control = {
          propertyType: 'bacnet',
          propertyId: bacnetControl?.propertyId || '',
        };
      } else if (integrationType.value === 'modbus') {
        payload.control = {
          propertyType: 'modbus',
          propertyId: modbusControl?.propertyId || '',
        };
      } else if (integrationType.value === 'mqtt') {
        payload.control = {
          propertyType: 'mqtt',
          propertyId: mqttControl?.propertyId || '',
        };
      } else if (integrationType.value === 'matter') {
        // Always include control
        payload.control = {
          propertyType: 'matter',
          propertyId: matterControl?.propertyId || '',
          clusterId: matterControl?.clusterId || 0,
          attributeId: matterControl?.attributeId || 0,
        };
      } else if (integrationType.value === 'esphome') {
        const transformerConfig = buildESPHomeTransformerConfig({}, esphomeControl?.field);
        payload.control = {
          propertyType: 'esphome',
          esphomeEntityId: esphomeControl?.esphomeEntityId || '',
          entityKey: esphomeControl?.entityKey || undefined,
          transformerConfig,
        };
      } else if (integrationType.value === 'zwave') {
        payload.control = {
          propertyType: 'zwave',
          zwaveValueId: zwaveControl?.zwaveValueId || '',
          zwaveEndpointId: zwaveControl?.zwaveEndpointId || undefined,
        };
      } else if (integrationType.value === 'zigbee') {
        payload.control = {
          propertyType: 'zigbee',
          zigbeeAttributeId: zigbeeControl?.zigbeeAttributeId || '',
          zigbeeEndpointId: zigbeeControl?.zigbeeEndpointId || undefined,
        };
      } else if (integrationType.value === 'homeassistant') {
        payload.control = {
          propertyType: 'homeassistant',
          haEntityId: haControl?.haEntityId || '',
          transformerConfig: buildHATransformerConfig(haControl),
        };
      }

      // Build status reference
      if (integrationType.value === 'knx') {
        // Always include status, even if empty (allows clearing existing references)
        payload.status = {
          propertyType: 'knx',
          groupAddress: (knxStatus?.groupAddress || '').trim(),
          dpt: knxStatus?.dpt || 1,
        };
      } else if (integrationType.value === 'bacnet') {
        payload.status = {
          propertyType: 'bacnet',
          propertyId: bacnetStatus?.propertyId || '',
        };
      } else if (integrationType.value === 'modbus') {
        payload.status = {
          propertyType: 'modbus',
          propertyId: modbusStatus?.propertyId || '',
        };
      } else if (integrationType.value === 'mqtt') {
        payload.status = {
          propertyType: 'mqtt',
          propertyId: mqttStatus?.propertyId || '',
        };
      } else if (integrationType.value === 'matter') {
        // Always include status, even if empty (allows clearing existing references)
        payload.status = {
          propertyType: 'matter',
          propertyId: matterStatus?.propertyId || '',
          clusterId: matterStatus?.clusterId || 0,
          attributeId: matterStatus?.attributeId || 0,
        };
      } else if (integrationType.value === 'esphome') {
        const transformerConfig = buildESPHomeTransformerConfig({}, esphomeStatus?.field);
        payload.status = {
          propertyType: 'esphome',
          esphomeEntityId: esphomeStatus?.esphomeEntityId || '',
          entityKey: esphomeStatus?.entityKey || undefined,
          transformerConfig,
        };
      } else if (integrationType.value === 'zwave') {
        payload.status = {
          propertyType: 'zwave',
          zwaveValueId: zwaveStatus?.zwaveValueId || '',
          zwaveEndpointId: zwaveStatus?.zwaveEndpointId || undefined,
        };
      } else if (integrationType.value === 'zigbee') {
        payload.status = {
          propertyType: 'zigbee',
          zigbeeAttributeId: zigbeeStatus?.zigbeeAttributeId || '',
          zigbeeEndpointId: zigbeeStatus?.zigbeeEndpointId || undefined,
        };
      } else if (integrationType.value === 'homeassistant') {
        payload.status = {
          propertyType: 'homeassistant',
          haEntityId: haStatus?.haEntityId || '',
          transformerConfig: buildHATransformerConfig(haStatus),
        };
      }

      // The editor only speaks for a binding it can both read and write: a
      // payload that carries no address of its own is dropped unless the value
      // is bound to this very protocol right now (empty fields for a value
      // bound to it = the operator clearing that binding). Otherwise the hub's
      // references route would delete a binding of another protocol — a demo
      // device's datapoint, which it cannot re-create — and the device would
      // stop being drivable (card t_4b64834a).
      if (payload.control && !shouldSendReferencePayload(payload.control, value.controlReference)) {
        console.log(
          `[DeviceEditor] Not sending the control reference for ${value.name}: the editor carries no address and the value is bound to ${value.controlReference?.propertyType ?? 'nothing'}`,
        );
        delete payload.control;
      }
      if (payload.status && !shouldSendReferencePayload(payload.status, value.statusReference)) {
        console.log(
          `[DeviceEditor] Not sending the status reference for ${value.name}: the editor carries no address and the value is bound to ${value.statusReference?.propertyType ?? 'nothing'}`,
        );
        delete payload.status;
      }

      // Only make API call if there are references to update
      if (Object.keys(payload).length > 0) {
        console.log(`[DeviceEditor] Sending PATCH to update references for ${value.name}:`, payload);
        await apiFetch(`/devices/${props.device.id}/values/${value.id}/references`, {
          method: 'PATCH',
          body: JSON.stringify(payload),
        });
      }
    } catch (error) {
      errors.push(`Failed to update ${value.name}: ${error}`);
    }
  }

  if (errors.length > 0) {
    console.error('Errors updating references:', errors);
    toast.error(t('dialogs.deviceRefsUpdateFailed', { errors: errors.join('\n') }));
    return false;
  }

  return true;
}

// Expose methods
defineExpose({
  validate,
  getData,
  saveReferences,
});
</script>
